import * as THREE from 'three';
import {
  MAP,
  CAMERA,
  CAR,
  PHYSICS,
  CAMERA_CHASE,
  GAME,
  AIRBORNE,
  SESSION_LENGTH_S,
  MUSIC,
  isInTunnelUnderpass,
  PERFORMANCE,
  TAKEDOWN,
  RUN_CONTRACTS,
} from '../utils/constants';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { createDirectorPostShaderPass } from './effects/createDirectorPostShaderPass';
import { FloatingScoreFeed } from '../ui/FloatingScoreFeed';
import { createCity } from './World';
import {
  applyDriverVisualVariant,
  createRealCar,
  DEFAULT_DRIVER_CAR_MODEL,
  updateCarLights,
  updateCarWheels,
} from './Car';
import { applyMaxAnisotropyToObject } from './textureUtils';
import {
  cloneVehicleInto,
  createVehicleState,
  stepVehiclePhysics,
  type VehicleInput,
  type VehicleState,
} from './vehicle';
import { integrateGroundStep } from './groundStep';
import { MultiplayerClient } from '../net/MultiplayerClient';
import {
  REMOTE_ENTITY,
  RemoteVehicleInterpolator,
  type VehicleSnapshot,
} from '../net/entityInterpolation';
import {
  resolveVehicleCollisions,
  resolveCopVehicleCollisions,
  resolveNPCTrafficCollision,
  forceUnstuckCop,
  invalidateBuildingAABBCache,
} from './collision';
import { constrainCopToGridRoad } from './roadNetwork';
import {
  resolveDestructibleCollisions,
  spawnDestructibleBurst,
  isPursuitBreaker,
  type DestructibleInstance,
} from './destructibles';
import {
  landVehicle,
  stepAirborne,
  tryLaunchFromRamps,
} from './ramps';
import { spawnVictoryCelebrationParticles } from './victoryCelebration';
import {
  isParkLot,
  isWorldXZOnGrassWithoutBuilding,
  tryBlockCellAtWorldXZ,
} from './mapLayout';
import { pickRoundSpawn } from './roundSpawn';
import { computeChaseInput, computeEvadeInput, resetCopAI } from './copAi';
import { applyCopCatchUp } from './copChase';
import { createBustMeterState, stepBustMeter, type BustMeterState } from './bustState';
import { GameAudio } from '../audio/GameAudio';
import { KeyboardDriveInput } from './input';
import { DrivingHud } from '../ui/DrivingHud';
import { drawMinimap, getMinimapScale } from '../ui/minimap';
import { ThreatIndicator } from '../ui/ThreatIndicator';
import { DrivingVfx } from './effects/DrivingVfx';
import { createSceneEnvironment } from './sceneEnvironment';
import { createPhysicalSky, type PhysicalSkyHandle } from './physicalSky';
import { createNightSky, type NightSkyHandle } from './nightSky';
import { createRainSystem, type RainSystemHandle } from './rain';
import { createStreetLamps, type StreetLampHandle } from './streetLamps';
import { createNeonSigns, type NeonSignHandle } from './neonSigns';
import { createWindowLights, type WindowLightsHandle } from './windowLights';
import {
  createPremiumCityAtmosphere,
  type PremiumCityAtmosphereHandle,
} from './premiumCityAtmosphere';
import {
  createPremiumRouteGuidance,
  type PremiumRouteGuidanceHandle,
} from './premiumRouteGuidance';
import { createParkedCars, type ParkedCarsHandle } from './parkedCars';
import {
  CameraDirector,
  type DirectorInput,
  type CameraDirectorOutput,
} from './CameraDirector';
import { createCopLightBar, type CopLightBarHandle } from './copLightBar';
import { createNPCTraffic, type NPCTrafficHandle } from './npcTraffic';
import {
  createPoliceHazards,
  type PoliceHazardsHandle,
} from './policeHazards';
import type { Room } from '@colyseus/sdk';
import type { ClientToServerMsg } from '../net/lobbyProtocol';
import {
  createHeatState,
  setHeatPoints,
  updateHeatState,
  type HeatState,
} from './heatSystem';
import { createHelicopter, type HelicopterHandle } from './helicopter';
import {
  createComboState,
  getComboHudDisplay,
  scoreComboEvent,
  formatComboSub,
  updateComboDecay,
  type ComboEventKind,
  type ComboState,
} from './comboSystem';
import { recordRun, type LeaderboardRow } from '../save/localLeaderboards';
import { recordDriverProgress } from '../save/driverProgress';
import { createRadialSpeedLinesMesh } from './effects/RadialSpeedLinesMesh';
import {
  driverLoadoutById,
  selectedDriverLoadoutId,
  type DriverLoadout,
} from './driverLoadouts';
import { gradeRun } from './runGrades';
import {
  RUN_MODE_STORAGE_KEY,
  RUN_CONTRACT_STORAGE_KEY,
  RUN_SEED_STORAGE_KEY,
  isRunContractComplete,
  pickRunContract,
  runContractBriefingLine,
  runContractBlockLine,
  runContractByKind,
  runModifierLine,
  runContractProgress,
  type RunContract,
  type RunContractStats,
  type RunModifierKind,
} from './runContracts';
import type { ScoreVariant } from '../ui/FloatingScoreFeed';

const CAR_Y = CAR.HEIGHT * 0.52;
const MP_SNAPSHOT_HZ = 20;

/** prd2 Day 19 — multiplayer round (roles + WS kept from lobby). */
export type GameViewOptions = {
  multiplayer?: {
    role: 'driver' | 'cop';
    room: Room;
    sessionId: string;
    seed: number;
    roomCode: string;
  };
};

const SKY = 0x6ca6cd;
/** Fog + tunnel lerp base — horizon tint for physical sky. */
const HORIZON_FOG = 0x8fafc8;

/**
 * prd2: driving + chase cam — Day 2: speed zoom, nitro FOV/tilt/shake, collision shake, speed vignette.
 */
export class GameView {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private readonly clock = new THREE.Clock();
  private readonly keyboard: KeyboardDriveInput;
  private readonly car: THREE.Group;
  private driverLoadout: DriverLoadout = driverLoadoutById('balanced');
  private readonly cityGroup: THREE.Group;
  private readonly landingShadow: THREE.Mesh;
  private vehicle = createVehicleState(
    0,
    MAP.HALF * 0.105,
    Math.PI,
  );
  private copVehicle = createVehicleState(
    GAME.COP_SPAWN_X,
    GAME.COP_SPAWN_Z,
    Math.PI,
  );
  private readonly copCar: THREE.Group;
  /** Quick play / SP only — two AI units chasing the driver. */
  private readonly dualCop: boolean;
  private cop2Vehicle: VehicleState | null = null;
  private cop2Car: THREE.Group | null = null;
  private readonly extractionRing: THREE.Mesh;
  private readonly extractionPulse: THREE.Mesh;
  private phase: 'playing' | 'won' | 'lost' = 'playing';
  private extractionHold = 0;
  private readonly restartHandler: (e: KeyboardEvent) => void;
  private readonly extractionFillEl: HTMLElement | null;
  private readonly cameraLookTarget = new THREE.Vector3();
  private readonly hud: DrivingHud;
  /** User-controlled multiplier on chase distance (starts a bit zoomed out). */
  private chaseZoom = 1.2;
  private targetChaseZoom = 1.2;
  private readonly wheelHandler: (e: WheelEvent) => void;
  private readonly keyZoomHandler: (e: KeyboardEvent) => void;
  private readonly pointerDownHandler: (e: PointerEvent) => void;
  private readonly pointerMoveHandler: (e: PointerEvent) => void;
  private readonly pointerUpHandler: (e: PointerEvent) => void;
  private readonly contextMenuHandler: (e: Event) => void;
  /** First canvas interaction or key starts Web Audio (Quick Play must not blast SFX). */
  private readonly audioUnlockHandler: (e: KeyboardEvent) => void;
  /** Left-drag: orbit camera around car (yaw / pitch offset). */
  private pointerLookActive = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private userYawOffset = 0;
  private userPitchOffset = 0;
  private minimapCtx: CanvasRenderingContext2D | null = null;
  private minimapCanvasEl: HTMLCanvasElement | null = null;
  private minimapListenersBound = false;
  /** World-space offset of minimap center from chase car (persists while you turn). */
  private minimapPanWorldX = 0;
  private minimapPanWorldZ = 0;
  private minimapDragging = false;
  private minimapDragPointerId = -1;
  private minimapLastClientX = 0;
  private minimapLastClientY = 0;
  private readonly minimapW = 220;
  private readonly minimapH = 220;
  private readonly threatIndicator = new ThreatIndicator();
  /** For threat arrows — closing speed. */
  private prevDistCopThreat = 400;
  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private smaaPass: SMAAPass | null = null;
  private directorPass: ShaderPass | null = null;
  private readonly smoothedDirectorFx = {
    chroma: 0,
    radial: 0,
    vig: 0,
    red: 0,
  };
  private readonly scoreFeed = new FloatingScoreFeed();
  private comboState: ComboState = createComboState();
  private radialSpeedLines: ReturnType<typeof createRadialSpeedLinesMesh> | null =
    null;
  /** Accumulates destructible hits this frame → heat + stats. */
  private propertyDestroyedAcc = 0;
  private pendingCopTakedown = false;
  private readonly copTakedownCooldownUntil = new Map<VehicleState, number>();
  private readonly copStunUntil = new Map<VehicleState, number>();
  private lastDriftScoreMilestone = 0;
  private readonly impactFlashEl: HTMLElement | null;
  private readonly hudClosingEl: HTMLElement | null;
  private readonly speedStreaksEl: HTMLElement | null;
  private physicalSky: PhysicalSkyHandle | null = null;
  private nightSky: NightSkyHandle | null = null;
  private rainSystem: RainSystemHandle | null = null;
  private streetLamps: StreetLampHandle | null = null;
  private neonSigns: NeonSignHandle | null = null;
  private windowLights: WindowLightsHandle | null = null;
  private premiumAtmosphere: PremiumCityAtmosphereHandle | null = null;
  private premiumRoute: PremiumRouteGuidanceHandle | null = null;
  private parkedCars: ParkedCarsHandle | null = null;
  private runStartCuePlayed = false;
  private npcTraffic: NPCTrafficHandle | null = null;
  private cameraDirector: CameraDirector | null = null;
  private lastCollisionTime = 0;
  private lastCollisionImpulse = 0;
  private useCameraDirector = true;
  private copLightBar: CopLightBarHandle | null = null;
  private cop2LightBar: CopLightBarHandle | null = null;
  private savedToneMapping: THREE.ToneMapping = THREE.ACESFilmicToneMapping;
  private savedToneMappingExposure = 1.24;
  private readonly usePostFx: boolean;
  private raf = 0;
  private resizeHandler: () => void;
  /** prd2 Day 1: body pitch from longitudinal g — squat / nose dive. */
  private prevFwdSpeed = 0;
  private smoothedLongAccel = 0;
  private bodyPitch = 0;
  /** Smoothed chase position (shake applied on top). */
  private readonly camSmoothPos = new THREE.Vector3();
  private readonly shakeOffset = new THREE.Vector3();
  private shakeIntensity = 0;
  private prevNitroBoosting = false;
  private readonly destructibles: DestructibleInstance[];
  private readonly drivingVfx: DrivingVfx;
  /** Heat level system - escalating police response */
  private heatState: HeatState = createHeatState();
  /** Pre-created cop pool to avoid lag when spawning */
  private copPool: { vehicle: VehicleState; car: THREE.Group; lightBar: CopLightBarHandle; active: boolean }[] = [];
  /** Max extra cops (level 5 = 5 total, minus 2 base = 3 extra) - kept small for performance */
  private readonly MAX_EXTRA_COPS = 3;
  /** Track collisions for heat gain */
  private lastCopCollisionFrame = -100;
  private lastNearMissFrame = -100;
  /** Police helicopter for heat level 5 */
  private helicopter: HelicopterHandle | null = null;
  private readonly policeHazards: PoliceHazardsHandle;
  private frameCount = 0;
  private sessionRemainingS = SESSION_LENGTH_S;
  private readonly audio = new GameAudio();
  private readonly gameOverlay: HTMLElement | null;
  private readonly gameOverlayTitle: HTMLElement | null;
  private readonly closeCallEl: HTMLElement | null;
  private bust: BustMeterState;
  private slowMoRemaining = 0;
  /** Near-miss bullet-time window length (seconds), used for easing. */
  private slowMoTotal = 0.45;
  /** Driver win: keep `phase === 'playing'` until this hits 0 so the camera director keeps running. */
  private victoryCelebrationRemain = 0;
  private victoryHeadlightT = 0;
  private victoryHeadlightRestore: {
    mat: THREE.Material;
    emissiveIntensity?: number;
    opacity?: number;
  }[] = [];
  /** Hit-stop: real-time seconds of strong dt scaling (pursuit breaker / takedown / nitro). */
  private juiceFreezeRemain = 0;
  private cinematicTimeScale = 1;
  private lastDirectorOutput: CameraDirectorOutput | null = null;
  private closeCallRemaining = 0;
  /** Last-frame snapshot for audio when `phase !== 'playing'`. */
  private lastAudioCopDist = 400;
  private lastAudioBustMeter = 0;
  private lastWantedStars = 0;
  /** PMREM environment map for PBR car paint / glass. */
  private envTexture: THREE.Texture | null = null;
  /** prd2 Day 17 — smooth remote cars from snapshot stream (game WebSocket). */
  private readonly remoteInterp = new RemoteVehicleInterpolator();
  private readonly mpSession: string | null;
  private readonly mpRole: 'driver' | 'cop' | null;
  /** prd2 Day 16 — client prediction + reconcile (use `?pred=1` + `npm run server`). */
  private predictionClient: MultiplayerClient | null = null;
  private inputSeq = 0;
  private readonly mpRoom: Room | null;
  private mpSnapAcc = 0;
  private outcomeSent = false;
  private mpPeerSnapshotMsg: ((msg: unknown) => void) | null = null;
  private mpRoundEndMsg: ((msg: unknown) => void) | null = null;
  private mpPeerDisconnectedMsg: (() => void) | null = null;
  /** prd2 Day 20 — peer left or link died; stop syncing and fall back to local AI. */
  private mpPeerDisconnected = false;
  /** Quick play: goal + spawns vary each round; MP / `?pred=1` use `GAME.EXTRACTION_*`. */
  private roundExtractionX: number = GAME.EXTRACTION_X;
  private roundExtractionZ: number = GAME.EXTRACTION_Z;
  private roundSpawnSeed = 0;
  /** `?debug=1` — tunnel flag + coords for testing underpass / layout. */
  private readonly debugHud: boolean;
  private readonly hemiLight: THREE.HemisphereLight;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GAME STATS - tracked during gameplay for victory screen
  // ═══════════════════════════════════════════════════════════════════════════
  private stats = {
    maxSpeedKmh: 0,
    totalDriftDistance: 0,
    closestCopDistance: Infinity,
    copsEvaded: 0,
    takedowns: 0,
    nearMisses: 0,
    totalCollisions: 0,
    /** Cumulative chaos “$” from props (hits + destroys). */
    propertyDamage: 0,
    pursuitBreakersTriggered: 0,
    startTime: performance.now(),
  };
  private lastDriftPos = { x: 0, z: 0 };
  private wasDrifting = false;
  private runContract: RunContract = pickRunContract(0, true);
  private runMode: 'standard' | 'daily' = 'standard';
  private contractCompleteAnnounced = false;
  private lastContractBlockFrame = -1000;
  private readonly contractCueKeys = new Set<string>();
  private runModifiersApplied = false;
  private readonly ambientLight: THREE.AmbientLight;
  private readonly sunLight: THREE.DirectionalLight;
  private readonly fillLight: THREE.DirectionalLight;
  private readonly fogColorBase = new THREE.Color(0x8fafc8);
  private readonly fogColorTunnel = new THREE.Color(0x505860);
  private readonly fogNearBase = 300;
  private readonly fogFarBase = 1200;
  /** Smoothed 0–1 under elevated underpass (ambient + fog). */
  private tunnelBlend = 0;

  constructor(
    private readonly container: HTMLElement,
    options?: GameViewOptions,
  ) {
    invalidateBuildingAABBCache();
    this.hud = new DrivingHud();
    const sid = document.body.dataset.mpSession;
    this.mpSession = sid && sid.length > 0 ? sid : null;
    const mr = document.body.dataset.mpRole;
    this.mpRole = mr === 'driver' || mr === 'cop' ? mr : null;
    this.mpRoom = options?.multiplayer?.room ?? null;
    const predParamOn =
      new URLSearchParams(window.location.search).get('pred') === '1';
    this.debugHud =
      new URLSearchParams(window.location.search).get('debug') === '1';
    this.dualCop = this.mpSession == null && !predParamOn;
    if (predParamOn) {
      const wsUrl =
        import.meta.env.VITE_PRED_WS_URL ?? 'ws://localhost:8765';
      this.predictionClient = new MultiplayerClient(wsUrl);
      const pc = this.predictionClient;
      pc.connect((vehicles) => {
        cloneVehicleInto(this.vehicle, vehicles[pc.playerId]);
        const other = pc.playerId === 0 ? 1 : 0;
        cloneVehicleInto(this.copVehicle, vehicles[other]);
        this.copCar.position.set(this.copVehicle.x, CAR_Y, this.copVehicle.z);
        this.copCar.rotation.y = this.copVehicle.heading;
        this.initChaseCamera();
      });
    }
    this.gameOverlay = document.getElementById('game-end-overlay');
    this.gameOverlayTitle = document.getElementById('game-end-title');
    this.closeCallEl = document.getElementById('close-call-toast');
    this.impactFlashEl = document.getElementById('impact-flash');
    this.hudClosingEl = document.getElementById('hud-closing-in');
    this.speedStreaksEl = document.getElementById('speed-streaks');
    this.bust = createBustMeterState();

    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(max-width: 900px)').matches;
    const dprCap = isMobile
      ? PERFORMANCE.MOBILE_PIXEL_RATIO_MAX
      : PERFORMANCE.DESKTOP_PIXEL_RATIO_MAX;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.usePostFx =
      PERFORMANCE.POST_PROCESSING_ENABLED && !reduceMotion && !isMobile;

    this.renderer = new THREE.WebGLRenderer({
      antialias: !isMobile,
      powerPreference: 'high-performance',
      alpha: false,
      stencil: false,
      depth: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, dprCap));
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setClearColor(0x000000, 1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.24;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.container.appendChild(this.renderer.domElement);
    this.container.dataset.speed = 'low';

    this.wheelHandler = (e: WheelEvent) => {
      this.unlockAudioFromInteraction();
      if (e.ctrlKey) return;
      e.preventDefault();
      const step = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      this.targetChaseZoom = THREE.MathUtils.clamp(
        this.targetChaseZoom * step,
        CAMERA_CHASE.ZOOM_MIN,
        CAMERA_CHASE.ZOOM_MAX,
      );
    };
    const canvas = this.renderer.domElement;
    canvas.tabIndex = 0;
    canvas.style.touchAction = 'none';
    canvas.style.outline = 'none';

    this.pointerDownHandler = (e: PointerEvent) => {
      this.unlockAudioFromInteraction();
      if (e.button === 0) {
        this.pointerLookActive = true;
        this.lastPointerX = e.clientX;
        this.lastPointerY = e.clientY;
        canvas.setPointerCapture(e.pointerId);
      }
      canvas.focus({ preventScroll: true });
    };
    this.pointerMoveHandler = (e: PointerEvent) => {
      if (!this.pointerLookActive || (e.buttons & 1) === 0) return;
      const dx = e.clientX - this.lastPointerX;
      const dy = e.clientY - this.lastPointerY;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
      this.userYawOffset -= dx * 0.0028;
      this.userPitchOffset += dy * 0.0018;
      this.userPitchOffset = THREE.MathUtils.clamp(
        this.userPitchOffset,
        -0.42,
        0.55,
      );
    };
    this.pointerUpHandler = (e: PointerEvent) => {
      if (e.type === 'pointercancel' || e.button === 0) {
        this.pointerLookActive = false;
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
    };
    this.contextMenuHandler = (e: Event) => e.preventDefault();

    this.audioUnlockHandler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      this.unlockAudioFromInteraction();
    };
    window.addEventListener('keydown', this.audioUnlockHandler, true);

    canvas.addEventListener('pointerdown', this.pointerDownHandler);
    canvas.addEventListener('pointermove', this.pointerMoveHandler);
    canvas.addEventListener('pointerup', this.pointerUpHandler);
    canvas.addEventListener('pointercancel', this.pointerUpHandler);
    canvas.addEventListener('contextmenu', this.contextMenuHandler);

    canvas.addEventListener('wheel', this.wheelHandler, {
      passive: false,
    });

    this.keyZoomHandler = (e: KeyboardEvent) => {
      if (
        e.code === 'BracketLeft' ||
        e.code === 'Minus' ||
        e.code === 'NumpadSubtract'
      ) {
        e.preventDefault();
        this.targetChaseZoom = THREE.MathUtils.clamp(
          this.targetChaseZoom * 1.12,
          CAMERA_CHASE.ZOOM_MIN,
          CAMERA_CHASE.ZOOM_MAX,
        );
      }
      if (
        e.code === 'BracketRight' ||
        e.code === 'Equal' ||
        e.code === 'NumpadAdd'
      ) {
        e.preventDefault();
        this.targetChaseZoom = THREE.MathUtils.clamp(
          this.targetChaseZoom / 1.12,
          CAMERA_CHASE.ZOOM_MIN,
          CAMERA_CHASE.ZOOM_MAX,
        );
      }
    };
    window.addEventListener('keydown', this.keyZoomHandler, { capture: true });

    this.scene = new THREE.Scene();
    this.scene.background = null;
    this.scene.fog = new THREE.Fog(
      HORIZON_FOG,
      this.fogNearBase,
      this.fogFarBase,
    );
    this.envTexture = createSceneEnvironment(this.renderer);
    this.scene.environment = this.envTexture;

    this.camera = new THREE.PerspectiveCamera(
      CAMERA_CHASE.BASE_FOV,
      this.container.clientWidth / Math.max(this.container.clientHeight, 1),
      CAMERA.NEAR,
      CAMERA.FAR,
    );

    const rsl = createRadialSpeedLinesMesh();
    this.radialSpeedLines = rsl;
    rsl.group.position.set(0, 0, -3.15);
    this.camera.add(rsl.group);

    this.keyboard = new KeyboardDriveInput();

    this.setupMinimapCanvas();

    this.hemiLight = new THREE.HemisphereLight(0x87ceeb, 0x444444, 0.4);
    this.scene.add(this.hemiLight);
    this.ambientLight = new THREE.AmbientLight(0x404050, 0.3);
    this.scene.add(this.ambientLight);

    const h = MAP.HALF;
    this.sunLight = new THREE.DirectionalLight(0xfff0d0, 0.9);
    this.sunLight.position.set(h * 0.6, h * 0.8, h * 0.4);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(1024, 1024);
    this.sunLight.shadow.bias = -0.0003;
    this.sunLight.shadow.normalBias = 0.04;
    this.sunLight.shadow.camera.near = 40;
    this.sunLight.shadow.camera.far = 600;
    const ext = 200;
    this.sunLight.shadow.camera.left = -ext;
    this.sunLight.shadow.camera.right = ext;
    this.sunLight.shadow.camera.top = ext;
    this.sunLight.shadow.camera.bottom = -ext;
    this.scene.add(this.sunLight);

    this.fillLight = new THREE.DirectionalLight(0x6080a0, 0.2);
    this.fillLight.position.set(-h * 0.7, h * 0.4, -h * 0.3);
    this.scene.add(this.fillLight);

    this.physicalSky = createPhysicalSky();
    this.scene.add(this.physicalSky.mesh);
    this.physicalSky.loadHDRI(this.renderer, this.scene);
    this.physicalSky.syncSun(this.sunLight.position);

    this.nightSky = createNightSky();
    this.nightSky.group.visible = false;
    this.scene.add(this.nightSky.group);

    this.rainSystem = createRainSystem();
    this.rainSystem.group.visible = false;
    this.scene.add(this.rainSystem.group);

    this.cameraDirector = new CameraDirector();

    this.cityGroup = createCity(42);
    this.scene.add(this.cityGroup);
    this.destructibles =
      (this.cityGroup.userData.destructibleInstances as
        | DestructibleInstance[]
        | undefined) ?? [];

    this.streetLamps = createStreetLamps();
    this.streetLamps.group.visible = false;
    this.scene.add(this.streetLamps.group);

    this.neonSigns = createNeonSigns(42);
    this.neonSigns.group.visible = false;
    this.scene.add(this.neonSigns.group);

    this.windowLights = createWindowLights(42);
    this.windowLights.mesh.visible = false;
    this.scene.add(this.windowLights.mesh);

    this.premiumAtmosphere = createPremiumCityAtmosphere(42);
    this.premiumAtmosphere.group.visible = false;
    this.scene.add(this.premiumAtmosphere.group);

    const atmosphere =
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('lm-atmosphere')
        : null;
    if (atmosphere === 'storm') {
      this.nightSky.group.visible = true;
      this.rainSystem.group.visible = true;
      this.windowLights.mesh.visible = true;
      this.streetLamps.group.visible = true;
      this.neonSigns.group.visible = true;
      this.premiumAtmosphere.group.visible = true;
      if (this.physicalSky?.mesh) this.physicalSky.mesh.visible = false;
      this.ambientLight.intensity = 0.14;
      this.sunLight.intensity = 0.08;
      this.fillLight.intensity = 0.06;
      this.scene.background = new THREE.Color(0x05070e);
      try {
        sessionStorage.removeItem('lm-atmosphere');
      } catch {
        /* ignore */
      }
    }

    this.parkedCars = createParkedCars(42);
    this.parkedCars.group.visible = atmosphere === 'storm';
    this.scene.add(this.parkedCars.group);

    this.npcTraffic = createNPCTraffic(42, 12);
    this.scene.add(this.npcTraffic.group);

    this.policeHazards = createPoliceHazards();
    this.scene.add(this.policeHazards.group);

    this.driverLoadout = driverLoadoutById(
      this.mpInSession() ? 'balanced' : selectedDriverLoadoutId(),
    );
    this.car = createRealCar('driver', DEFAULT_DRIVER_CAR_MODEL);
    applyDriverVisualVariant(this.car, this.driverLoadout);
    this.car.rotation.order = 'YXZ';
    this.car.position.set(this.vehicle.x, CAR_Y, this.vehicle.z);
    this.car.rotation.y = this.vehicle.heading;
    this.scene.add(this.car);
    this.drivingVfx = new DrivingVfx(this.scene);

    this.landingShadow = new THREE.Mesh(
      new THREE.CircleGeometry(3.2, 28),
      new THREE.MeshBasicMaterial({
        color: 0x040608,
        transparent: true,
        opacity: 0.42,
        depthWrite: false,
      }),
    );
    this.landingShadow.rotation.x = -Math.PI / 2;
    this.landingShadow.visible = false;
    this.scene.add(this.landingShadow);

    this.copCar = createRealCar('cop');
    this.copCar.rotation.order = 'YXZ';
    this.copCar.position.set(this.copVehicle.x, CAR_Y, this.copVehicle.z);
    this.copCar.rotation.y = this.copVehicle.heading;
    this.scene.add(this.copCar);

    this.copLightBar = createCopLightBar();
    this.scene.add(this.copLightBar.group);

    if (this.dualCop) {
      this.cop2Vehicle = createVehicleState(
        GAME.COP2_SPAWN_X,
        GAME.COP2_SPAWN_Z,
        Math.PI,
      );
      this.cop2Car = createRealCar('cop');
      this.cop2Car.rotation.order = 'YXZ';
      this.cop2Car.position.set(this.cop2Vehicle.x, CAR_Y, this.cop2Vehicle.z);
      this.cop2Car.rotation.y = this.cop2Vehicle.heading;
      this.scene.add(this.cop2Car);

      this.cop2LightBar = createCopLightBar();
      this.scene.add(this.cop2LightBar.group);
    }

    // Pre-create cop pool to avoid lag when heat level increases
    for (let i = 0; i < this.MAX_EXTRA_COPS; i++) {
      const vehicle = createVehicleState(-9999, -9999, 0); // Off-map initially
      const car = createRealCar('cop');
      car.rotation.order = 'YXZ';
      car.position.set(-9999, CAR_Y, -9999);
      car.visible = false; // Hidden until activated
      this.scene.add(car);

      const lightBar = createCopLightBar();
      lightBar.group.visible = false;
      this.scene.add(lightBar.group);

      this.copPool.push({ vehicle, car, lightBar, active: false });
    }

    // Initialize helicopter (async, will be ready when needed)
    createHelicopter(this.scene).then((heli) => {
      this.helicopter = heli;
      console.log('Helicopter loaded and ready!');
    }).catch((err) => {
      console.error('Failed to create helicopter:', err);
    });

    applyMaxAnisotropyToObject(this.scene, this.renderer);

    const extR = GAME.EXTRACTION_RADIUS;
    const ringOuter = new THREE.Mesh(
      new THREE.RingGeometry(extR * 0.78, extR * 1.04, 72),
      new THREE.MeshBasicMaterial({
        color: 0x22ffaa,
        transparent: true,
        opacity: 0.55,
        side: THREE.DoubleSide,
      }),
    );
    ringOuter.rotation.x = -Math.PI / 2;
    ringOuter.position.set(GAME.EXTRACTION_X, 0.07, GAME.EXTRACTION_Z);
    this.scene.add(ringOuter);
    this.extractionRing = ringOuter;

    const extractionPulse = new THREE.Mesh(
      new THREE.CircleGeometry(extR * 0.76, 48),
      new THREE.MeshBasicMaterial({
        color: 0x11ff88,
        transparent: true,
        opacity: 0.12,
        side: THREE.DoubleSide,
      }),
    );
    extractionPulse.rotation.x = -Math.PI / 2;
    extractionPulse.position.set(GAME.EXTRACTION_X, 0.06, GAME.EXTRACTION_Z);
    this.scene.add(extractionPulse);
    this.extractionPulse = extractionPulse;

    this.premiumRoute = createPremiumRouteGuidance();
    this.scene.add(this.premiumRoute.group);

    this.runMode = this.readSelectedRunMode();
    this.roundSpawnSeed = this.readSelectedRunSeed() ?? ((Math.random() * 0xffffffff) >>> 0);
    if (this.dualCop) {
      this.applyRandomSinglePlayerSpawns();
    }
    this.pickNewRunContract(true);

    this.extractionFillEl = document.getElementById('extraction-hold-fill');
    this.restartHandler = (e: KeyboardEvent) => {
      if (e.code === 'KeyR' && this.phase !== 'playing') {
        e.preventDefault();
        this.resetRound();
      }
    };
    window.addEventListener('keydown', this.restartHandler, { capture: true });

    this.container.dataset.gamePhase = 'playing';

    this.initChaseCamera();

    this.savedToneMapping = this.renderer.toneMapping;
    this.savedToneMappingExposure = this.renderer.toneMappingExposure;
    if (this.usePostFx) {
      this.renderer.toneMapping = THREE.NoToneMapping;
      const composer = new EffectComposer(this.renderer);
      this.composer = composer;
      const w0 = this.container.clientWidth;
      const ch0 = Math.max(this.container.clientHeight, 1);
      composer.addPass(new RenderPass(this.scene, this.camera));
      this.directorPass = createDirectorPostShaderPass();
      composer.addPass(this.directorPass);
      composer.addPass(new OutputPass());
      composer.setSize(w0, ch0);
      composer.setPixelRatio(Math.min(window.devicePixelRatio, dprCap));
    }

    this.resizeHandler = () => {
      const w = this.container.clientWidth;
      const ch = Math.max(this.container.clientHeight, 1);
      this.camera.aspect = w / ch;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, ch);
      this.composer?.setSize(w, ch);
      this.composer?.setPixelRatio(
        Math.min(window.devicePixelRatio, dprCap),
      );
      this.bloomPass?.setSize(w, ch);
      this.smaaPass?.setSize(w, ch);
    };
    window.addEventListener('resize', this.resizeHandler);

    this.loop = this.loop.bind(this);
    this.raf = requestAnimationFrame(this.loop);

    requestAnimationFrame(() => {
      canvas.focus({ preventScroll: true });
      this.setupMinimapCanvas();
    });

    if (this.mpInSession() && this.mpRoom) {
      const r = this.mpRoom;
      this.mpPeerSnapshotMsg = (raw: unknown) => {
        const msg = raw as {
          x?: number;
          z?: number;
          vx?: number;
          vz?: number;
          heading?: number;
        };
        const x = Number(msg.x);
        const z = Number(msg.z);
        const vx = Number(msg.vx);
        const vz = Number(msg.vz);
        const heading = Number(msg.heading);
        if (
          !Number.isFinite(x) ||
          !Number.isFinite(z) ||
          !Number.isFinite(vx) ||
          !Number.isFinite(vz) ||
          !Number.isFinite(heading)
        ) {
          return;
        }
        this.pushRemoteVehicleSnapshot({
          t: performance.now(),
          x,
          z,
          vx,
          vz,
          heading,
        });
      };
      r.onMessage('peer_snapshot', this.mpPeerSnapshotMsg);

      this.mpRoundEndMsg = (raw: unknown) => {
        const msg = raw as {
          youWon?: boolean;
          title?: string;
          reason?: string;
        };
        if (this.phase !== 'playing') return;
        this.outcomeSent = true;
        const title = typeof msg.title === 'string' ? msg.title : '';
        if (msg.youWon) {
          this.phase = 'won';
          this.showEndOverlay(title);
        } else {
          const reason =
            msg.reason === 'bust'
              ? 'bust'
              : msg.reason === 'time_up'
                ? 'time_up'
                : 'driver_escaped';
          this.beginLossSequence(title, reason);
        }
      };
      r.onMessage('round_end', this.mpRoundEndMsg);

      this.mpPeerDisconnectedMsg = () => {
        this.handlePeerFallbackFromDisconnect();
      };
      r.onMessage('peer_disconnected', this.mpPeerDisconnectedMsg);
    }
  }

  /** prd2 Day 20 — keep driving with AI opponent; idempotent. */
  private handlePeerFallbackFromDisconnect(): void {
    if (this.mpPeerDisconnected || this.phase !== 'playing') return;
    if (!this.mpInSession()) return;
    this.mpPeerDisconnected = true;
    this.remoteInterp.clear();
    const el = document.getElementById('mp-peer-toast');
    if (el) {
      el.hidden = false;
      el.dataset.state = 'show';
      window.setTimeout(() => {
        el.hidden = true;
        el.dataset.state = 'hide';
      }, 4200);
    }
  }

  private readonly minimapPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    const mm = this.minimapCanvasEl;
    if (!mm) return;
    this.unlockAudioFromInteraction();
    e.preventDefault();
    e.stopPropagation();
    this.minimapDragging = true;
    this.minimapDragPointerId = e.pointerId;
    this.minimapLastClientX = e.clientX;
    this.minimapLastClientY = e.clientY;
    mm.classList.add('minimap-dragging');
    try {
      mm.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  private readonly minimapPointerMove = (e: PointerEvent): void => {
    if (!this.minimapDragging || e.pointerId !== this.minimapDragPointerId)
      return;
    if ((e.buttons & 1) === 0) return;
    const dx = e.clientX - this.minimapLastClientX;
    const dy = e.clientY - this.minimapLastClientY;
    this.minimapLastClientX = e.clientX;
    this.minimapLastClientY = e.clientY;
    const scale = getMinimapScale(this.minimapW, this.minimapH);
    const h = this.chaseVehicle().heading;
    const cos = Math.cos(h);
    const sin = Math.sin(h);
    const inv = 1 / scale;
    this.minimapPanWorldX += inv * (-dx * cos + dy * sin);
    this.minimapPanWorldZ += inv * (dx * sin + dy * cos);
  };

  private readonly minimapPointerUp = (e: PointerEvent): void => {
    if (!this.minimapDragging) return;
    const mm = this.minimapCanvasEl;
    this.minimapDragging = false;
    this.minimapDragPointerId = -1;
    mm?.classList.remove('minimap-dragging');
    try {
      if (mm) mm.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  private readonly minimapDblClick = (e: MouseEvent): void => {
    e.preventDefault();
    this.minimapPanWorldX = 0;
    this.minimapPanWorldZ = 0;
  };

  /** Minimap canvas may exist after first paint; safe to call multiple times. */
  private setupMinimapCanvas(): void {
    if (this.minimapCtx) return;
    const mm = document.getElementById('minimap') as HTMLCanvasElement | null;
    if (!mm) return;
    const dpr = Math.min(window.devicePixelRatio, 2);
    mm.width = this.minimapW * dpr;
    mm.height = this.minimapH * dpr;
    mm.style.width = `${this.minimapW}px`;
    mm.style.height = `${this.minimapH}px`;
    const mctx = mm.getContext('2d', { alpha: false });
    if (!mctx) return;
    mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.minimapCtx = mctx;
    this.minimapCanvasEl = mm;
    if (!this.minimapListenersBound) {
      this.minimapListenersBound = true;
      mm.addEventListener('pointerdown', this.minimapPointerDown);
      mm.addEventListener('pointermove', this.minimapPointerMove);
      mm.addEventListener('pointerup', this.minimapPointerUp);
      mm.addEventListener('pointercancel', this.minimapPointerUp);
      mm.addEventListener('dblclick', this.minimapDblClick);
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('keydown', this.restartHandler, { capture: true });
    window.removeEventListener('keydown', this.audioUnlockHandler, true);
    window.removeEventListener('keydown', this.keyZoomHandler, {
      capture: true,
    });
    if (this.minimapCanvasEl && this.minimapListenersBound) {
      const mm = this.minimapCanvasEl;
      mm.removeEventListener('pointerdown', this.minimapPointerDown);
      mm.removeEventListener('pointermove', this.minimapPointerMove);
      mm.removeEventListener('pointerup', this.minimapPointerUp);
      mm.removeEventListener('pointercancel', this.minimapPointerUp);
      mm.removeEventListener('dblclick', this.minimapDblClick);
      this.minimapListenersBound = false;
    }
    this.minimapCanvasEl = null;
    this.minimapCtx = null;
    this.drivingVfx.dispose();
    this.audio.dispose();
    const canvas = this.renderer.domElement;
    canvas.removeEventListener('wheel', this.wheelHandler);
    canvas.removeEventListener('pointerdown', this.pointerDownHandler);
    canvas.removeEventListener('pointermove', this.pointerMoveHandler);
    canvas.removeEventListener('pointerup', this.pointerUpHandler);
    canvas.removeEventListener('pointercancel', this.pointerUpHandler);
    canvas.removeEventListener('contextmenu', this.contextMenuHandler);
    this.keyboard.dispose();
    this.predictionClient?.dispose();
    if (this.mpRoom) {
      void this.mpRoom.leave();
      this.mpPeerSnapshotMsg = null;
      this.mpRoundEndMsg = null;
      this.mpPeerDisconnectedMsg = null;
    }
    this.envTexture?.dispose();
    this.envTexture = null;
    this.scene.environment = null;
    this.bloomPass?.dispose();
    this.bloomPass = null;
    this.smaaPass?.dispose();
    this.smaaPass = null;
    this.directorPass?.dispose();
    this.directorPass = null;
    if (this.radialSpeedLines) {
      this.camera.remove(this.radialSpeedLines.group);
      this.radialSpeedLines.dispose();
      this.radialSpeedLines = null;
    }
    this.composer?.dispose();
    this.composer = null;
    this.renderer.toneMapping = this.savedToneMapping;
    this.renderer.toneMappingExposure = this.savedToneMappingExposure;
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private applyRandomSinglePlayerSpawns(): void {
    if (this.runMode !== 'daily') {
      this.roundSpawnSeed =
        (this.roundSpawnSeed ^ (Date.now() & 0xffffffff)) >>> 0;
    }
    const s = pickRoundSpawn(this.roundSpawnSeed);
    this.roundExtractionX = s.extractionX;
    this.roundExtractionZ = s.extractionZ;
    this.extractionRing.position.set(this.roundExtractionX, 0.07, this.roundExtractionZ);
    this.extractionPulse.position.set(this.roundExtractionX, 0.06, this.roundExtractionZ);
    this.vehicle = createVehicleState(
      s.driverX,
      s.driverZ,
      s.driverHeading,
    );
    this.copVehicle = createVehicleState(
      s.cop1X,
      s.cop1Z,
      s.cop1Heading,
    );
    if (this.cop2Vehicle) {
      this.cop2Vehicle = createVehicleState(
        s.cop2X,
        s.cop2Z,
        s.cop2Heading,
      );
    }
    this.car.position.set(this.vehicle.x, CAR_Y, this.vehicle.z);
    this.car.rotation.y = this.vehicle.heading;
    this.car.rotation.x = 0;
    this.copCar.position.set(this.copVehicle.x, CAR_Y, this.copVehicle.z);
    this.copCar.rotation.y = this.copVehicle.heading;
    this.copCar.rotation.x = 0;
    if (this.cop2Car && this.cop2Vehicle) {
      this.cop2Car.position.set(this.cop2Vehicle.x, CAR_Y, this.cop2Vehicle.z);
      this.cop2Car.rotation.y = this.cop2Vehicle.heading;
      this.cop2Car.rotation.x = 0;
    }
  }

  private currentContractStats(): RunContractStats {
    return {
      takedowns: this.stats.takedowns,
      propertyDamage: this.stats.propertyDamage,
      pursuitBreakers: this.stats.pursuitBreakersTriggered,
      stylePoints: this.comboState.totalStylePoints,
      elapsedS: (performance.now() - this.stats.startTime) / 1000,
    };
  }

  private showMissionBriefing(): void {
    const wrap = document.getElementById('mission-briefing');
    const title = document.getElementById('mission-briefing-title');
    const copy = document.getElementById('mission-briefing-copy');
    const status = document.getElementById('mission-briefing-status');
    if (!wrap) return;
    if (title) title.textContent = this.runContract.title;
    if (copy) {
      copy.textContent = `${runContractBriefingLine(this.runContract)} Modifiers: ${runModifierLine(this.runContract)}.`;
    }
    if (status) status.textContent = 'Route beacons arming';
    wrap.dataset.state = 'show';
    window.setTimeout(() => {
      if (status && this.phase === 'playing') {
        status.textContent = this.isExtractionUnlocked()
          ? 'Extraction route open'
          : 'Contract gate active';
      }
    }, 1150);
    window.setTimeout(() => {
      if (wrap) wrap.dataset.state = 'hide';
    }, 4400);
  }

  private readSelectedRunSeed(): number | null {
    if (this.mpInSession() || typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(RUN_SEED_STORAGE_KEY);
    if (!raw) return null;
    const seed = Number(raw);
    return Number.isFinite(seed) ? seed >>> 0 : null;
  }

  private readSelectedRunMode(): 'standard' | 'daily' {
    if (this.mpInSession() || typeof sessionStorage === 'undefined') return 'standard';
    return sessionStorage.getItem(RUN_MODE_STORAGE_KEY) === 'daily' ? 'daily' : 'standard';
  }

  private hasRunModifier(kind: RunModifierKind): boolean {
    return this.runContract.modifiers.some((m) => m.kind === kind);
  }

  private applyRunModifiersOnRoundStart(): void {
    if (this.runModifiersApplied || this.mpInSession()) return;
    this.runModifiersApplied = true;

    if (this.hasRunModifier('hot_grid')) {
      setHeatPoints(this.heatState, this.heatState.points + 42);
    }
    if (this.hasRunModifier('short_fuse')) {
      this.sessionRemainingS = Math.min(
        this.sessionRemainingS,
        RUN_CONTRACTS.FAST_ESCAPE_TARGET_S + 12,
      );
    }
  }

  private pickNewRunContract(showIntro: boolean): void {
    const selectedMode =
      !this.mpInSession() && typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(RUN_MODE_STORAGE_KEY)
        : null;
    if (selectedMode === 'daily') this.runMode = 'daily';
    const selectedKind =
      !this.mpInSession() && typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(RUN_CONTRACT_STORAGE_KEY)
        : null;
    this.runContract =
      runContractByKind(selectedKind) ??
      pickRunContract(this.roundSpawnSeed, this.mpInSession());
    if (selectedKind) {
      try {
        sessionStorage.removeItem(RUN_CONTRACT_STORAGE_KEY);
        sessionStorage.removeItem(RUN_SEED_STORAGE_KEY);
        sessionStorage.removeItem(RUN_MODE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    this.contractCompleteAnnounced = false;
    this.lastContractBlockFrame = -1000;
    this.contractCueKeys.clear();
    this.runModifiersApplied = false;
    this.applyRunModifiersOnRoundStart();
    if (!showIntro) return;
    window.setTimeout(() => {
      if (this.phase !== 'playing') return;
      this.showMissionBriefing();
      this.addCameraShake(0.16);
      this.audio.bumpMusicImpactDuck(0.2);
    }, 260);
    window.setTimeout(() => {
      if (this.phase !== 'playing') return;
      this.scoreFeed.show(
        this.runMode === 'daily' ? `DAILY: ${this.runContract.title}` : this.runContract.title,
        `${this.driverLoadout.name} armed. ${this.runContract.briefing} ${runModifierLine(this.runContract)} active.`,
        this.runContract.kind === 'escape' ? 'neutral' : 'heat',
        undefined,
        'mega',
      );
    }, 960);
    window.setTimeout(() => {
      if (this.phase !== 'playing') return;
      this.scoreFeed.show(
        'ROUTE BEACONS ONLINE',
        'Follow the holograms. Finish the contract before extraction.',
        'good',
        undefined,
        'mega',
      );
    }, 2200);
    window.setTimeout(() => {
      if (this.phase !== 'playing') return;
      this.scoreFeed.show(
        'DESKTOP CONTROLS',
        'WASD drive · Shift nitro · Space drift · R restart',
        'neutral',
      );
    }, 3600);
  }

  private isExtractionUnlocked(): boolean {
    return isRunContractComplete(this.runContract, this.currentContractStats());
  }

  private maybeAnnounceContractComplete(): void {
    if (this.contractCompleteAnnounced || !this.isExtractionUnlocked()) return;
    this.contractCompleteAnnounced = true;
    if (this.runContract.kind === 'escape' || this.runContract.kind === 'rush') return;
    this.stylePop(
      'heat_up',
      'CONTRACT COMPLETE',
      this.runContract.completeLine,
      'heat',
      220,
    );
  }

  private showExtractionLocked(): void {
    if (
      this.frameCount - this.lastContractBlockFrame <
      RUN_CONTRACTS.EXTRACTION_BLOCK_COOLDOWN_FRAMES
    ) {
      return;
    }
    this.lastContractBlockFrame = this.frameCount;
    this.extractionHold = 0;
    if (this.extractionFillEl) this.extractionFillEl.style.transform = 'scaleX(0)';
    this.scoreFeed.show(
      'CONTRACT NOT DONE',
      runContractBlockLine(this.runContract, this.currentContractStats()),
      'danger',
      undefined,
      'mega',
    );
    this.addCameraShake(0.18);
    this.audio.bumpMusicImpactDuck(0.26);
  }

  private maybeShowContractRunCue(): void {
    if (this.phase !== 'playing' || this.victoryCelebrationRemain > 0) return;
    const stats = this.currentContractStats();
    const cue = (() => {
      if (
        this.hasRunModifier('clean_delivery') &&
        this.stats.totalCollisions >= 2 &&
        stats.elapsedS > 10
      ) {
        return {
          key: 'mod_clean_delivery',
          title: 'CLEAN DELIVERY AT RISK',
          line: 'Collisions hurt your handler grade. Keep the package clean.',
          variant: 'danger' as const,
        };
      }
      if (
        this.hasRunModifier('hot_grid') &&
        this.heatState.level >= 2 &&
        stats.elapsedS > 8
      ) {
        return {
          key: 'mod_hot_grid',
          title: 'HOT GRID ACTIVE',
          line: 'Police pressure is ahead of schedule. Use side streets and breakers.',
          variant: 'heat' as const,
        };
      }
      if (
        this.hasRunModifier('style_sponsor') &&
        stats.elapsedS > 14 &&
        stats.stylePoints < RUN_CONTRACTS.STYLE_POINTS_TARGET * 0.35
      ) {
        return {
          key: 'mod_style_sponsor',
          title: 'SPONSOR FEED IS COLD',
          line: 'Start chaining drifts, near misses, and takedowns.',
          variant: 'good' as const,
        };
      }
      switch (this.runContract.kind) {
        case 'takedown': {
          if (stats.elapsedS > 10 && stats.takedowns === 0) {
            return {
              key: 'takedown_open',
              title: 'BREAK THEIR LINE',
              line: 'Side-slam, PIT, or brake-check a unit before extraction.',
              variant: 'heat' as const,
            };
          }
          if (stats.takedowns >= 2 && stats.takedowns < RUN_CONTRACTS.TAKEDOWN_TARGET) {
            return {
              key: 'takedown_final',
              title: 'ONE MORE UNIT',
              line: 'Route control is waiting for the last takedown.',
              variant: 'great' as const,
            };
          }
          break;
        }
        case 'chaos': {
          const half = RUN_CONTRACTS.CHAOS_BILL_TARGET * 0.5;
          if (stats.elapsedS > 12 && stats.propertyDamage < half) {
            return {
              key: 'chaos_open',
              title: 'MAKE NOISE',
              line: 'Clip signs, barriers, and street assets to open extraction.',
              variant: 'heat' as const,
            };
          }
          if (stats.propertyDamage >= half && stats.propertyDamage < RUN_CONTRACTS.CHAOS_BILL_TARGET) {
            return {
              key: 'chaos_half',
              title: 'CITY IS WATCHING',
              line: 'Keep the damage chain alive.',
              variant: 'great' as const,
            };
          }
          break;
        }
        case 'breaker':
          if (stats.elapsedS > 12 && stats.pursuitBreakers < RUN_CONTRACTS.PURSUIT_BREAKER_TARGET) {
            return {
              key: 'breaker_open',
              title: 'FIND A BREAKER',
              line: 'Use marked city hazards to wipe the chase lane.',
              variant: 'heat' as const,
            };
          }
          break;
        case 'style': {
          const half = RUN_CONTRACTS.STYLE_POINTS_TARGET * 0.5;
          if (stats.elapsedS > 10 && stats.stylePoints < half) {
            return {
              key: 'style_open',
              title: 'STYLE GATE',
              line: 'Drift, near-miss, and chain takedowns for sponsor points.',
              variant: 'good' as const,
            };
          }
          if (stats.stylePoints >= half && stats.stylePoints < RUN_CONTRACTS.STYLE_POINTS_TARGET) {
            return {
              key: 'style_half',
              title: 'CHAIN IS LIVE',
              line: 'Do not let the multiplier die now.',
              variant: 'great' as const,
            };
          }
          break;
        }
        case 'rush':
          if (
            stats.elapsedS > RUN_CONTRACTS.FAST_ESCAPE_TARGET_S - 18 &&
            stats.elapsedS <= RUN_CONTRACTS.FAST_ESCAPE_TARGET_S
          ) {
            return {
              key: 'rush_window',
              title: 'BONUS WINDOW CLOSING',
              line: 'Commit to the route. No scenic driving.',
              variant: 'danger' as const,
            };
          }
          break;
        case 'escape':
          if (stats.elapsedS > 16 && this.getNearestCopDist() < 85) {
            return {
              key: 'escape_pressure',
              title: 'LOCKDOWN MOVING',
              line: 'Keep speed through the beacon lane.',
              variant: 'heat' as const,
            };
          }
          break;
      }
      return null;
    })();

    if (!cue || this.contractCueKeys.has(cue.key)) return;
    this.contractCueKeys.add(cue.key);
    this.scoreFeed.show(cue.title, cue.line, cue.variant, undefined, 'mega');
    this.audio.bumpMusicImpactDuck(0.18);
  }

  private resetRound(): void {
    if (this.predictionClient?.connected) return;
    this.outcomeSent = false;
    this.mpPeerDisconnected = false;
    this.mpSnapAcc = 0;
    this.remoteInterp.clear();
    this.phase = 'playing';
    this.runStartCuePlayed = false;
    this.container.removeAttribute('data-loss-reason');
    if (this.gameOverlay) this.gameOverlay.removeAttribute('data-loss-reason');
    resetCopAI();
    this.extractionHold = 0;
    this.container.dataset.gamePhase = 'playing';
    this.prevFwdSpeed = 0;
    this.smoothedLongAccel = 0;
    this.bodyPitch = 0;
    if (this.dualCop) {
      this.applyRandomSinglePlayerSpawns();
    } else {
      this.roundExtractionX = GAME.EXTRACTION_X;
      this.roundExtractionZ = GAME.EXTRACTION_Z;
      this.extractionRing.position.set(GAME.EXTRACTION_X, 0.07, GAME.EXTRACTION_Z);
      this.extractionPulse.position.set(GAME.EXTRACTION_X, 0.06, GAME.EXTRACTION_Z);
      this.vehicle = createVehicleState(0, MAP.HALF * 0.105, Math.PI);
      this.copVehicle = createVehicleState(
        GAME.COP_SPAWN_X,
        GAME.COP_SPAWN_Z,
        Math.PI,
      );
      if (this.cop2Vehicle) {
        this.cop2Vehicle = createVehicleState(
          GAME.COP2_SPAWN_X,
          GAME.COP2_SPAWN_Z,
          Math.PI,
        );
      }
      this.car.position.set(this.vehicle.x, CAR_Y, this.vehicle.z);
      this.car.rotation.y = this.vehicle.heading;
      this.car.rotation.x = 0;
      this.copCar.position.set(this.copVehicle.x, CAR_Y, this.copVehicle.z);
      this.copCar.rotation.y = this.copVehicle.heading;
      this.copCar.rotation.x = 0;
      if (this.cop2Car && this.cop2Vehicle) {
        this.cop2Car.position.set(this.cop2Vehicle.x, CAR_Y, this.cop2Vehicle.z);
        this.cop2Car.rotation.y = this.cop2Vehicle.heading;
        this.cop2Car.rotation.x = 0;
      }
    }
    const overlay = document.getElementById('game-end-overlay');
    if (overlay) {
      overlay.hidden = true;
      overlay.dataset.result = '';
    }
    const holdWrap = document.getElementById('extraction-hold');
    if (holdWrap) holdWrap.hidden = true;
    if (this.extractionFillEl) this.extractionFillEl.style.transform = 'scaleX(0)';
    this.heatState = createHeatState();
    this.policeHazards.reset();
    // Deactivate all pool cops when resetting
    for (const ec of this.copPool) {
      ec.active = false;
      ec.car.visible = false;
      ec.lightBar.group.visible = false;
      ec.vehicle.x = -9999;
      ec.vehicle.z = -9999;
    }
    this.sessionRemainingS = SESSION_LENGTH_S;
    this.bust = createBustMeterState();
    this.prevDistCopThreat = 400;
    this.slowMoRemaining = 0;
    this.restoreVictoryHeadlights();
    this.victoryCelebrationRemain = 0;
    this.victoryHeadlightT = 0;
    this.juiceFreezeRemain = 0;
    this.container.removeAttribute('data-juice-punch');
    this.closeCallRemaining = 0;
    if (this.closeCallEl) this.closeCallEl.dataset.state = 'hide';
    this.audio.resetMusicPhase();
    
    // Reset game stats for new round
    this.stats = {
      maxSpeedKmh: 0,
      totalDriftDistance: 0,
      closestCopDistance: Infinity,
      copsEvaded: 0,
      takedowns: 0,
      nearMisses: 0,
      totalCollisions: 0,
      /** Cumulative chaos “$” from props (hits + destroys). */
      propertyDamage: 0,
      pursuitBreakersTriggered: 0,
      startTime: performance.now(),
    };
    this.lastDriftScoreMilestone = 0;
    this.propertyDestroyedAcc = 0;
    this.pendingCopTakedown = false;
    this.copTakedownCooldownUntil.clear();
    this.copStunUntil.clear();
    this.comboState = createComboState();
    this.wasDrifting = false;
    this.pickNewRunContract(false);
    
    this.minimapPanWorldX = 0;
    this.minimapPanWorldZ = 0;
    this.initChaseCamera();
    window.setTimeout(() => {
      if (this.phase !== 'playing') return;
      const roleLine =
        this.mpRole === 'cop'
          ? 'Stop the driver before extraction'
          : this.runContract.intro;
      this.scoreFeed.show('THE HEIST WENT WRONG', roleLine, 'danger', undefined, 'mega');
    }, 520);
  }

  private snapshotVictoryHeadlights(): void {
    this.restoreVictoryHeadlights();
    this.victoryHeadlightRestore = [];
    this.car.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) {
        if (m instanceof THREE.MeshPhysicalMaterial) {
          const eh = m.emissive?.getHex?.() ?? 0;
          if (eh >= 0xf0f0f0 && m.emissiveIntensity > 0.35) {
            this.victoryHeadlightRestore.push({
              mat: m,
              emissiveIntensity: m.emissiveIntensity,
            });
          }
        } else if (m instanceof THREE.MeshBasicMaterial && m.transparent) {
          const hx = m.color.getHex();
          if (hx >= 0xfff0e0 && hx <= 0xffffff) {
            this.victoryHeadlightRestore.push({ mat: m, opacity: m.opacity });
          }
        }
      }
    });
  }

  private restoreVictoryHeadlights(): void {
    for (const s of this.victoryHeadlightRestore) {
      if (
        s.mat instanceof THREE.MeshPhysicalMaterial &&
        s.emissiveIntensity !== undefined
      ) {
        s.mat.emissiveIntensity = s.emissiveIntensity;
      } else if (
        s.mat instanceof THREE.MeshBasicMaterial &&
        s.opacity !== undefined
      ) {
        s.mat.opacity = s.opacity;
      }
    }
    this.victoryHeadlightRestore = [];
  }

  private updateVictoryHeadlightPulse(t: number): void {
    const pulse = 0.52 + 0.48 * Math.sin(t * 13.2);
    const wobble = 0.78 + 0.22 * Math.sin(t * 7.1);
    for (const s of this.victoryHeadlightRestore) {
      if (
        s.mat instanceof THREE.MeshPhysicalMaterial &&
        s.emissiveIntensity !== undefined
      ) {
        s.mat.emissiveIntensity =
          s.emissiveIntensity * pulse * 1.72 * wobble;
      } else if (
        s.mat instanceof THREE.MeshBasicMaterial &&
        s.opacity !== undefined
      ) {
        s.mat.opacity = Math.min(1, s.opacity * (0.42 + pulse * 1.18));
      }
    }
  }

  /** Starts particles, director shot, slow-mo; `phase` stays `playing` until timer ends. */
  private beginDriverVictoryCelebration(): void {
    if (this.victoryCelebrationRemain > 0) return;
    this.victoryCelebrationRemain = 3.4;
    this.victoryHeadlightT = 0;
    this.snapshotVictoryHeadlights();
    this.cameraDirector?.triggerVictoryCelebration();
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    spawnVictoryCelebrationParticles(
      this.scene,
      this.vehicle.x,
      CAR_Y + this.vehicle.airY + 0.5,
      this.vehicle.z,
      reducedMotion,
      this.vehicle.heading,
    );
    this.slowMoTotal = 1.0;
    this.slowMoRemaining = 1.0;
    this.triggerHitStop(0.052, 'victory');
    const holdWrap = document.getElementById('extraction-hold');
    if (holdWrap) holdWrap.hidden = true;
  }

  private finishVictoryCelebrationDriver(): void {
    this.restoreVictoryHeadlights();
    this.phase = 'won';
    this.showEndOverlay(this.runContract.title);
  }

  private stageCopsForBust(): void {
    if (this.predictionClient?.connected || this.mpSync()) return;
    const slots = [
      { angle: 0.05, dist: 7.4 },
      { angle: Math.PI * 0.72, dist: 8.6 },
      { angle: -Math.PI * 0.72, dist: 8.6 },
      { angle: Math.PI, dist: 10.2 },
      { angle: Math.PI * 0.38, dist: 9.6 },
    ];
    const px = this.vehicle.x;
    const pz = this.vehicle.z;
    const baseHeading = this.vehicle.heading;
    const staged: Array<{
      vehicle: VehicleState;
      car: THREE.Group;
      lightBar: CopLightBarHandle | null;
    }> = [{ vehicle: this.copVehicle, car: this.copCar, lightBar: this.copLightBar }];

    if (this.cop2Vehicle && this.cop2Car) {
      staged.push({
        vehicle: this.cop2Vehicle,
        car: this.cop2Car,
        lightBar: this.cop2LightBar,
      });
    }

    for (const ec of this.copPool) {
      if (staged.length >= slots.length) break;
      ec.active = true;
      ec.car.visible = true;
      ec.lightBar.group.visible = true;
      staged.push({ vehicle: ec.vehicle, car: ec.car, lightBar: ec.lightBar });
    }

    for (let i = 0; i < staged.length; i++) {
      const slot = slots[i]!;
      const a = baseHeading + slot.angle;
      const x = px + Math.sin(a) * slot.dist;
      const z = pz + Math.cos(a) * slot.dist;
      const item = staged[i]!;
      item.vehicle.x = x;
      item.vehicle.z = z;
      item.vehicle.vx = 0;
      item.vehicle.vz = 0;
      item.vehicle.yawRate = 0;
      item.vehicle.heading = Math.atan2(px - x, pz - z);
      item.car.position.set(x, CAR_Y, z);
      item.car.rotation.y = item.vehicle.heading;
      item.car.rotation.x = 0;
      item.lightBar?.setPosition(x, CAR_Y, z, item.vehicle.heading);
    }
  }

  private beginLossSequence(
    title: string,
    reason: 'bust' | 'time_up' | 'driver_escaped',
  ): void {
    if (this.phase !== 'playing') return;
    this.phase = 'lost';
    this.container.dataset.lossReason = reason;
    if (this.gameOverlay) this.gameOverlay.dataset.lossReason = reason;

    if (reason === 'bust') {
      this.stageCopsForBust();
      this.slowMoTotal = 0.85;
      this.slowMoRemaining = 0.85;
      this.triggerHitStop(0.1, 'busted');
      this.triggerImpactFlash(0.34);
      this.addCameraShake(0.95);
      this.audio.triggerCollision(0.85);
      this.audio.bumpMusicImpactDuck(1);
      this.scoreFeed.show('BOXED IN', 'Police closed every exit', 'danger', undefined, 'mega');
    } else if (reason === 'time_up') {
      this.triggerHitStop(0.055, 'timeup');
      this.addCameraShake(0.42);
      this.scoreFeed.show('EXTRACTION LOST', 'Timer hit zero', 'danger', undefined, 'mega');
    }

    this.showEndOverlay(title);
  }

  private showEndOverlay(title: string): void {
    this.container.dataset.gamePhase = this.phase === 'won' ? 'won' : 'lost';
    if (this.gameOverlayTitle) this.gameOverlayTitle.textContent = title;
    if (this.gameOverlay) {
      this.gameOverlay.hidden = false;
      this.gameOverlay.dataset.result = this.phase;
      
      // Update stats display for victory screen
      if (this.phase === 'won') {
        const elapsedS = (performance.now() - this.stats.startTime) / 1000;
        const timeBonus = Math.max(0, Math.floor((SESSION_LENGTH_S - elapsedS) * 100));
        const grade = gradeRun(this.runContract, {
          ...this.currentContractStats(),
          elapsedS,
          maxSpeedKmh: this.stats.maxSpeedKmh,
          nearMisses: this.stats.nearMisses,
          closestCopDistance: this.stats.closestCopDistance,
          completed: true,
          totalCollisions: this.stats.totalCollisions,
        });
        this.updateStatElement('run-grade-mark', grade.grade);
        this.updateStatElement('run-grade-label', `${grade.label} · ${grade.score}/100`);
        this.updateStatElement('run-grade-detail', grade.detail);
        const progress = recordDriverProgress({
          grade,
          stylePoints: this.comboState.totalStylePoints,
          takedowns: this.stats.takedowns,
          pursuitBreakers: this.stats.pursuitBreakersTriggered,
          nearMisses: this.stats.nearMisses,
          completed: true,
        });
        this.updateStatElement(
          'driver-rank-label',
          `Rank ${progress.rankAfter} · ${progress.rankTitle}`,
        );
        this.updateStatElement(
          'driver-xp-earned',
          `${progress.leveledUp ? 'Level up! ' : ''}+${progress.xpEarned.toLocaleString()} XP`,
        );
        this.updateStatElement(
          'driver-progress-meta',
          `${(progress.after.totalXp - progress.currentRankXp).toLocaleString()} / ${(progress.nextRankXp - progress.currentRankXp).toLocaleString()} XP to next rank`,
        );
        const progressFill = document.getElementById('driver-progress-fill');
        if (progressFill) {
          progressFill.style.transform = `scaleX(${progress.progress01.toFixed(3)})`;
        }
        
        this.updateStatElement('stat-max-speed', `${Math.floor(this.stats.maxSpeedKmh)} km/h`);
        this.updateStatElement('stat-drift-distance', `${Math.floor(this.stats.totalDriftDistance)}m`);
        this.updateStatElement('stat-closest-call', 
          this.stats.closestCopDistance === Infinity ? 'N/A' : `${this.stats.closestCopDistance.toFixed(1)}m`);
        this.updateStatElement('stat-near-misses', `${this.stats.nearMisses}`);
        this.updateStatElement('stat-time-bonus', `+${timeBonus}`);
        this.updateStatElement('stat-completion-time', `${elapsedS.toFixed(1)}s`);
        this.updateStatElement(
          'stat-cops-evaded',
          `${this.stats.copsEvaded}`,
        );
        this.updateStatElement(
          'stat-property-damage',
          `$${this.stats.propertyDamage.toLocaleString()}`,
        );
        this.updateStatElement(
          'stat-pursuit-breakers',
          `${this.stats.pursuitBreakersTriggered}`,
        );
        this.updateStatElement(
          'stat-style-points',
          `${Math.floor(this.comboState.totalStylePoints)}`,
        );

        const lb = recordRun(
          this.comboState.totalStylePoints,
          elapsedS,
          this.stats.pursuitBreakersTriggered,
          this.comboState.peakMultiplier,
        );
        this.fillLeaderboardOverlay(lb.board);
        const rankEl = document.getElementById('victory-lb-rank');
        if (rankEl) {
          rankEl.textContent = lb.isTopScore
            ? 'New personal best!'
            : `Run rank #${lb.rank} (local)`;
        }

        // Play victory audio
        this.audio.playVictoryAudio();
      }
    }
  }
  
  private updateStatElement(id: string, value: string): void {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  }

  private stylePop(
    kind: ComboEventKind,
    title: string,
    flavor: string,
    variant: ScoreVariant,
    overrideBase?: number,
  ): void {
    const r = scoreComboEvent(this.comboState, kind, overrideBase);
    const mega =
      kind === 'pursuit_breaker' || kind === 'takedown' ? 'mega' : 'normal';
    this.scoreFeed.show(title, flavor, variant, formatComboSub(r), mega);
  }

  /** Screen + time feel — chase-loop juice (not new mechanics). */
  private juicePunchScreen(tag: string): void {
    this.container.dataset.juicePunch = tag;
    window.setTimeout(() => {
      if (this.container.dataset.juicePunch === tag) {
        this.container.removeAttribute('data-juice-punch');
      }
    }, 240);
  }

  private triggerHitStop(seconds: number, punchTag: string): void {
    this.juiceFreezeRemain = Math.max(this.juiceFreezeRemain, seconds);
    this.juicePunchScreen(punchTag);
  }

  private triggerImpactFlash(durationS: number): void {
    if (!this.impactFlashEl) return;
    this.impactFlashEl.dataset.state = 'burst';
    window.setTimeout(() => {
      if (this.impactFlashEl) this.impactFlashEl.dataset.state = 'hide';
    }, durationS * 1000);
  }

  private fillLeaderboardOverlay(rows: LeaderboardRow[]): void {
    const ol = document.getElementById('victory-lb-list');
    const wrap = document.getElementById('victory-leaderboard');
    if (!ol || !wrap) return;
    ol.replaceChildren();
    const top = rows.slice(0, 6);
    if (top.length === 0) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    for (let i = 0; i < top.length; i++) {
      const r = top[i]!;
      const li = document.createElement('li');
      li.className = 'victory-lb__row';
      li.innerHTML = `<span class="victory-lb__rank">#${i + 1}</span><span class="victory-lb__pts">${r.stylePoints.toLocaleString()} pts</span><span class="victory-lb__meta">${r.timeS.toFixed(1)}s · ×${r.maxMult.toFixed(2)}</span>`;
      ol.appendChild(li);
    }
  }

  /** Lobby multiplayer session (driver/cop role), excluding prediction test mode. */
  private mpInSession(): boolean {
    if (this.predictionClient?.connected) return false;
    return this.mpSession != null && this.mpSession.length > 0 && this.mpRole != null;
  }

  /** Both players connected — apply remote snapshots / relay. */
  private mpSync(): boolean {
    return this.mpInSession() && !this.mpPeerDisconnected;
  }

  /** Local player — camera, HUD, VFX, body pitch (driver or cop in MP). */
  private chaseVehicle(): VehicleState {
    return this.mpRole === 'cop' ? this.copVehicle : this.vehicle;
  }

  /** Other car for minimap / HUD (local driver → cop; local cop → driver). */
  private opponentVehicle(): VehicleState {
    return this.mpRole === 'cop' ? this.vehicle : this.copVehicle;
  }

  /**
   * prd2 Day 17 — feed from game sync when peer snapshots arrive (`t` should share a clock with `performance.now()` or be offset-synced).
   */
  pushRemoteVehicleSnapshot(snap: VehicleSnapshot): void {
    if (!this.mpSync()) return;
    const id =
      this.mpRole === 'driver' ? REMOTE_ENTITY.COP : REMOTE_ENTITY.DRIVER;
    this.remoteInterp.pushFrame(id, snap);
  }

  private sendRoundOutcome(
    winner: 'driver' | 'cop',
    reason: 'extraction' | 'bust' | 'time_up',
  ): void {
    if (this.outcomeSent || !this.mpRoom || !this.mpSession) return;
    this.outcomeSent = true;
    const msg: ClientToServerMsg = {
      type: 'round_outcome',
      sessionId: this.mpSession,
      winner,
      reason,
    };
    try {
      this.mpRoom.send('round_outcome', {
        sessionId: msg.sessionId,
        winner: msg.winner,
        reason: msg.reason,
      });
    } catch {
      /* ignore — connection may be closing */
    }
  }

  private pushApart(a: VehicleState, b: VehicleState): void {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    const d = Math.hypot(dx, dz) || 1e-6;
    const min = CAR.COLLISION_RADIUS * 2.12;
    if (d >= min) return;
    const push = (min - d) * 0.5;
    const nx = dx / d;
    const nz = dz / d;
    b.x -= nx * push;
    b.z -= nz * push;
    a.x += nx * push;
    a.z += nz * push;
  }

  /** Police stay on grid roads — snap off sidewalks / building edges; re-resolve collisions. */
  private applyCopRoadConstraint(): void {
    const px = this.vehicle.x;
    const pz = this.vehicle.z;

    let c = constrainCopToGridRoad(
      this.copVehicle.x,
      this.copVehicle.z,
      this.copVehicle.vx,
      this.copVehicle.vz,
      px,
      pz,
    );
    let r = resolveCopVehicleCollisions(c.x, c.z, c.vx, c.vz, px, pz);
    this.copVehicle.x = r.x;
    this.copVehicle.z = r.z;
    this.copVehicle.vx = r.vx;
    this.copVehicle.vz = r.vz;

    if (this.dualCop && this.cop2Vehicle) {
      c = constrainCopToGridRoad(
        this.cop2Vehicle.x,
        this.cop2Vehicle.z,
        this.cop2Vehicle.vx,
        this.cop2Vehicle.vz,
        px,
        pz,
      );
      r = resolveCopVehicleCollisions(c.x, c.z, c.vx, c.vz, px, pz);
      this.cop2Vehicle.x = r.x;
      this.cop2Vehicle.z = r.z;
      this.cop2Vehicle.vx = r.vx;
      this.cop2Vehicle.vz = r.vz;
    }
  }

  private separateCopPlayer(): void {
    if (this.mpSync()) return;
    this.pushApart(this.vehicle, this.copVehicle);
    if (this.dualCop && this.cop2Vehicle) {
      this.pushApart(this.vehicle, this.cop2Vehicle);
      this.pushApart(this.copVehicle, this.cop2Vehicle);
    }
  }

  /** Nearest pursuing unit for evade / distance checks when `dualCop`. */
  private nearestCopTo(x: number, z: number): VehicleState {
    if (!this.dualCop || !this.cop2Vehicle) return this.copVehicle;
    const d1 = Math.hypot(x - this.copVehicle.x, z - this.copVehicle.z);
    const d2 = Math.hypot(x - this.cop2Vehicle.x, z - this.cop2Vehicle.z);
    return d1 <= d2 ? this.copVehicle : this.cop2Vehicle;
  }

  /** Get all active cop vehicles including extras from heat system */
  private getAllCops(): VehicleState[] {
    const cops: VehicleState[] = [this.copVehicle];
    if (this.dualCop && this.cop2Vehicle) {
      cops.push(this.cop2Vehicle);
    }
    for (const ec of this.copPool) {
      if (ec.active) cops.push(ec.vehicle);
    }
    return cops;
  }

  /** Big destructible hit: slam nearby cops so pursuit breakers actually matter. */
  /** Returns how many cop units were hit (for score / heat takedown). */
  private applyPursuitBreakerShockwave(x: number, z: number, radius: number): number {
    const cops = this.getAllCops();
    let hits = 0;
    for (const cop of cops) {
      const dx = cop.x - x;
      const dz = cop.z - z;
      const d = Math.hypot(dx, dz);
      if (d > radius || d < 1e-4) continue;
      hits++;
      const n = 1 - d / radius;
      const nx = dx / d;
      const nz = dz / d;
      const impulse = 8 + n * 20;
      cop.vx += nx * impulse;
      cop.vz += nz * impulse;
      cop.vx *= 0.24;
      cop.vz *= 0.24;
      cop.x += nx * (0.8 + n * 2.6);
      cop.z += nz * (0.8 + n * 2.6);
    }
    return hits;
  }

  private isCopStunned(cop: VehicleState): boolean {
    return (this.copStunUntil.get(cop) ?? 0) > this.frameCount;
  }

  private triggerCopTakedown(
    cop: VehicleState,
    nx: number,
    nz: number,
    label: string,
    flavor: string,
    severity: number,
  ): void {
    const sideX = -nz;
    const sideZ = nx;
    const impulse =
      TAKEDOWN.IMPULSE_BASE + TAKEDOWN.IMPULSE_SIDE * THREE.MathUtils.clamp(severity, 0, 1);
    const copRightX = Math.cos(cop.heading);
    const copRightZ = -Math.sin(cop.heading);
    const spinSign = copRightX * nx + copRightZ * nz >= 0 ? 1 : -1;
    cop.vx = cop.vx * 0.18 + nx * impulse + sideX * impulse * 0.42;
    cop.vz = cop.vz * 0.18 + nz * impulse + sideZ * impulse * 0.42;
    cop.yawRate += spinSign * (2.8 + severity * 3.8);
    cop.x += nx * 1.4;
    cop.z += nz * 1.4;
    this.copStunUntil.set(
      cop,
      this.frameCount + Math.round(TAKEDOWN.COP_STUN_S * 60),
    );
    this.copTakedownCooldownUntil.set(
      cop,
      this.frameCount + TAKEDOWN.COOLDOWN_FRAMES,
    );

    this.stats.copsEvaded += 1;
    this.stats.takedowns += 1;
    this.pendingCopTakedown = true;
    this.addCameraShake(0.72);
    this.triggerImpactFlash(0.28);
    this.slowMoTotal = 0.58;
    this.slowMoRemaining = 0.58;
    this.triggerHitStop(0.086, 'takedown');
    this.audio.triggerCollision(1);
    this.audio.bumpMusicImpactDuck(0.82);
    this.cameraDirector?.triggerPursuitBreakerCam(
      (this.vehicle.x + cop.x) * 0.5,
      (this.vehicle.z + cop.z) * 0.5,
    );
    this.stylePop(
      'takedown',
      label,
      flavor,
      'great',
      Math.round(TAKEDOWN.SCORE_BASE + severity * 260),
    );
  }

  private resolvePlayerCopTakedowns(input: VehicleInput): number {
    if (this.mpSync() || this.mpRole === 'cop' || this.phase !== 'playing') return 0;
    const playerSpeed = Math.hypot(this.vehicle.vx, this.vehicle.vz);
    if (playerSpeed < TAKEDOWN.PIT_SPEED_MS) return 0;

    const fwdX = Math.sin(this.vehicle.heading);
    const fwdZ = Math.cos(this.vehicle.heading);
    const rightX = Math.cos(this.vehicle.heading);
    const rightZ = -Math.sin(this.vehicle.heading);
    let count = 0;

    for (const cop of this.getAllCops()) {
      if ((this.copTakedownCooldownUntil.get(cop) ?? 0) > this.frameCount) continue;

      const dx = cop.x - this.vehicle.x;
      const dz = cop.z - this.vehicle.z;
      const dist = Math.hypot(dx, dz);
      if (dist <= 1e-4 || dist > TAKEDOWN.CONTACT_RADIUS) continue;

      const nx = dx / dist;
      const nz = dz / dist;
      const relVx = this.vehicle.vx - cop.vx;
      const relVz = this.vehicle.vz - cop.vz;
      const closing = relVx * nx + relVz * nz;
      const sideDot = Math.abs(rightX * nx + rightZ * nz);
      const frontDot = fwdX * nx + fwdZ * nz;
      const rearDot = -frontDot;
      const severity = THREE.MathUtils.clamp(
        (closing - TAKEDOWN.MIN_CLOSING_MS) / 18 + playerSpeed / 55,
        0,
        1,
      );

      if (
        rearDot > 0.55 &&
        dist < TAKEDOWN.BRAKE_CHECK_DIST &&
        closing > TAKEDOWN.BRAKE_CHECK_CLOSING_MS &&
        (input.throttle < -0.12 || input.handbrake)
      ) {
        this.triggerCopTakedown(cop, nx, nz, 'BRAKE CHECK', 'Cop ate your bumper', severity);
        count++;
        continue;
      }

      if (
        playerSpeed > TAKEDOWN.MIN_ATTACK_SPEED_MS &&
        closing > TAKEDOWN.MIN_CLOSING_MS &&
        sideDot > TAKEDOWN.SIDE_DOT_MIN
      ) {
        this.triggerCopTakedown(cop, nx, nz, 'SIDE SLAM', 'Police unit disabled', severity);
        count++;
        continue;
      }

      if (
        playerSpeed > TAKEDOWN.PIT_SPEED_MS &&
        closing > TAKEDOWN.MIN_CLOSING_MS * 0.72 &&
        frontDot > TAKEDOWN.PIT_REAR_DOT_MIN &&
        sideDot > 0.25
      ) {
        this.triggerCopTakedown(cop, nx, nz, 'PIT TAKEDOWN', 'Rear quarter hit', severity);
        count++;
      }
    }

    return count;
  }

  /** Get nearest cop distance */
  private getNearestCopDist(): number {
    const cops = this.getAllCops();
    let minDist = Infinity;
    for (const cop of cops) {
      const d = Math.hypot(this.vehicle.x - cop.x, this.vehicle.z - cop.z);
      if (d < minDist) minDist = d;
    }
    return minDist;
  }

  /** Activate/deactivate cops from pool based on heat level */
  private updateCopCountForHeat(): void {
    if (this.mpSync()) return; // Don't spawn extra cops in multiplayer

    const targetCount = this.heatState.config.copCount;
    const baseCops = this.dualCop ? 2 : 1;
    const extraNeeded = Math.max(0, Math.min(targetCount - baseCops, this.MAX_EXTRA_COPS));

    // Count currently active cops
    let activeCount = 0;
    for (const cop of this.copPool) {
      if (cop.active) activeCount++;
    }

    // Activate more cops if needed
    if (activeCount < extraNeeded) {
      for (const cop of this.copPool) {
        if (!cop.active && activeCount < extraNeeded) {
          // Spawn at a position around the player
          const angle = Math.random() * Math.PI * 2;
          const dist = 120 + Math.random() * 80;
          let x = this.vehicle.x + Math.cos(angle) * dist;
          let z = this.vehicle.z + Math.sin(angle) * dist;
          
          // Clamp to map bounds
          x = Math.max(-MAP.HALF + 50, Math.min(MAP.HALF - 50, x));
          z = Math.max(-MAP.HALF + 50, Math.min(MAP.HALF - 50, z));
          
          const heading = Math.atan2(this.vehicle.x - x, this.vehicle.z - z);
          
          cop.vehicle.x = x;
          cop.vehicle.z = z;
          cop.vehicle.heading = heading;
          cop.vehicle.vx = 0;
          cop.vehicle.vz = 0;
          
          cop.car.position.set(x, CAR_Y, z);
          cop.car.rotation.y = heading;
          cop.car.visible = true;
          cop.lightBar.group.visible = true;
          cop.active = true;
          activeCount++;
        }
      }
    }
  }

  /** Update all extra cops AI and physics - optimized for performance */
  private updateExtraCops(dt: number): void {
    const px = this.vehicle.x;
    const pz = this.vehicle.z;
    const boostMult = this.heatState.config.copSpeedBoost;
    
    // Collect active cops for separation pass
    let activeCount = 0;
    
    for (let i = 0; i < this.copPool.length; i++) {
      const ec = this.copPool[i]!;
      if (!ec.active) continue;
      activeCount++;

      // Simplified AI and physics
      const stunned = this.isCopStunned(ec.vehicle);
      const copInput = stunned
        ? { throttle: 0, steer: 0, handbrake: true, nitro: false }
        : computeChaseInput(ec.vehicle, this.vehicle);
      stepVehiclePhysics(ec.vehicle, copInput, dt);
      if (!stunned) applyCopCatchUp(ec.vehicle, dt * boostMult, px, pz);
      
      // Inline collision resolution (avoid function call overhead)
      const vx = ec.vehicle.vx;
      const vz = ec.vehicle.vz;
      ec.vehicle.x += vx * dt;
      ec.vehicle.z += vz * dt;

      // Update visuals - batch position updates
      const x = ec.vehicle.x;
      const z = ec.vehicle.z;
      const h = ec.vehicle.heading;
      ec.car.position.x = x;
      ec.car.position.z = z;
      ec.car.rotation.y = h;
      ec.lightBar.setPosition(x, CAR_Y, z, h);

      // Skip wheel animation for cops (performance)
      // Skip forceUnstuckCop for pool cops (base cops handle it)
      
      // Only separate from player (skip cop-cop separation for performance)
      this.pushApart(this.vehicle, ec.vehicle);
    }
    
    // Update light bars less frequently (every other frame)
    if (this.frameCount % 2 === 0) {
      for (const ec of this.copPool) {
        if (ec.active) ec.lightBar.update(dt * 2);
      }
    }
  }

  /** Stereo pan for siren — relative to camera forward. */
  private computeSirenPan(px: number, pz: number, cop: VehicleState): number {
    const toX = cop.x - px;
    const toZ = cop.z - pz;
    const len = Math.hypot(toX, toZ);
    if (len < 1e-3) return 0;
    const camDir = new THREE.Vector3();
    this.camera.getWorldDirection(camDir);
    camDir.y = 0;
    camDir.normalize();
    const to = new THREE.Vector3(toX / len, 0, toZ / len);
    const right = new THREE.Vector3(-camDir.z, 0, camDir.x);
    return THREE.MathUtils.clamp(to.dot(right), -1, 1);
  }

  private applyVehicleDamage(carRoot: THREE.Group, impulse: number): void {
    const meshes = carRoot.userData.paintMeshes as THREE.Mesh[] | undefined;
    if (!meshes) return;
    const bump = Math.min(0.055, impulse * 0.016);
    for (const m of meshes) {
      const mat = m.material as THREE.MeshPhysicalMaterial;
      if (mat?.roughness != null) {
        mat.roughness = Math.min(0.95, mat.roughness + bump);
      }
    }
  }

  private initChaseCamera(): void {
    const { HEIGHT, DISTANCE_BEHIND, LOOK_HEIGHT, BASE_FOV, TILT_DEGREES, TILT_HEIGHT_SCALE } =
      CAMERA_CHASE;
    const cv = this.chaseVehicle();
    const head = cv.heading + this.userYawOffset;
    const bx = cv.x;
    const bz = cv.z;
    const dist = DISTANCE_BEHIND * this.chaseZoom;
    const tilt = THREE.MathUtils.degToRad(TILT_DEGREES);
    const backX = -Math.sin(head) * dist;
    const backZ = -Math.cos(head) * dist;
    const camY =
      CAR_Y +
      HEIGHT +
      Math.sin(tilt) * dist * TILT_HEIGHT_SCALE +
      this.userPitchOffset * dist * 0.85;
    this.camSmoothPos.set(bx + backX, camY, bz + backZ);
    this.camera.position.copy(this.camSmoothPos);
    this.cameraLookTarget.set(bx, CAR_Y + LOOK_HEIGHT, bz);
    this.camera.lookAt(this.cameraLookTarget);
    this.camera.fov = BASE_FOV;
    this.camera.updateProjectionMatrix();
  }

  private addCameraShake(amount: number): void {
    this.shakeIntensity = Math.min(1, this.shakeIntensity + amount);
  }

  private syncChaseCamera(dt: number, nitroBoosting: boolean): void {
    const {
      HEIGHT,
      DISTANCE_BEHIND,
      TILT_DEGREES,
      TILT_HEIGHT_SCALE,
      LEAD_FACTOR,
      LEAD_SCALE,
      LEAD_LOOK_MIX,
      LOOK_HEIGHT,
      BASE_FOV,
      FOV_SPEED_EXTRA,
      FOV_NITRO_EXTRA,
      NITRO_TILT_EXTRA_DEG,
      ZOOM_AT_MAX_SPEED,
      SPEED_ZOOM_BLEND,
      SHAKE_DECAY,
      SHAKE_MAX_OFFSET,
      POSITION_SMOOTHING,
      ZOOM_SMOOTH,
    } = CAMERA_CHASE;

    this.shakeIntensity *= Math.pow(SHAKE_DECAY, dt * 60);

    const za = 1 - Math.exp(-ZOOM_SMOOTH * dt);
    this.chaseZoom = THREE.MathUtils.lerp(this.chaseZoom, this.targetChaseZoom, za);

    const cv = this.chaseVehicle();
    const speed = Math.hypot(cv.vx, cv.vz);
    const maxS = PHYSICS.NITRO_MAX_SPEED_MS;
    const speedRatio = maxS > 1e-6 ? Math.min(1, speed / maxS) : 0;

    const zoomK = 1 / Math.max(0.35, ZOOM_AT_MAX_SPEED);
    const distMul = 1 + (zoomK - 1) * speedRatio * SPEED_ZOOM_BLEND;
    const baseDist = DISTANCE_BEHIND * distMul * this.chaseZoom;

    const head = cv.heading + this.userYawOffset;
    const bx = cv.x;
    const bz = cv.z;
    const vx = cv.vx;
    const vz = cv.vz;
    const inv = speed > 0.15 ? 1 / speed : 0;
    const leadX = vx * inv * LEAD_FACTOR * LEAD_SCALE;
    const leadZ = vz * inv * LEAD_FACTOR * LEAD_SCALE;

    const nitroTiltAdd = nitroBoosting
      ? THREE.MathUtils.degToRad(NITRO_TILT_EXTRA_DEG)
      : 0;
    const tilt = THREE.MathUtils.degToRad(TILT_DEGREES) + nitroTiltAdd * 0.62;
    const backX = -Math.sin(head) * baseDist;
    const backZ = -Math.cos(head) * baseDist;
    const camY =
      CAR_Y +
      HEIGHT +
      Math.sin(tilt) * baseDist * TILT_HEIGHT_SCALE +
      this.userPitchOffset * baseDist * 0.85;

    if (!this.pointerLookActive) {
      this.userYawOffset *= 1 - Math.min(1, dt * 1.8);
    }

    const targetCam = new THREE.Vector3(
      bx + backX + leadX,
      camY,
      bz + backZ + leadZ,
    );
    const lookHead = cv.heading;
    const lookX = bx + Math.sin(lookHead) * 5 + leadX * LEAD_LOOK_MIX;
    const lookZ = bz + Math.cos(lookHead) * 5 + leadZ * LEAD_LOOK_MIX;
    const targetLook = new THREE.Vector3(lookX, CAR_Y + LOOK_HEIGHT, lookZ);

    const a = 1 - Math.exp(-POSITION_SMOOTHING * dt);
    this.camSmoothPos.lerp(targetCam, a);
    this.cameraLookTarget.lerp(targetLook, a);

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const sm = reduceMotion
      ? 0
      : SHAKE_MAX_OFFSET * this.shakeIntensity;
    this.shakeOffset.set(
      (Math.random() - 0.5) * 2 * sm,
      (Math.random() - 0.5) * 2 * sm * 0.42,
      (Math.random() - 0.5) * 2 * sm,
    );
    this.camera.position.copy(this.camSmoothPos).add(this.shakeOffset);
    this.camera.lookAt(this.cameraLookTarget);

    const fovTop =
      BASE_FOV + FOV_SPEED_EXTRA + (nitroBoosting ? FOV_NITRO_EXTRA : 0);
    const targetFov = THREE.MathUtils.lerp(BASE_FOV, fovTop, speedRatio);
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, targetFov, a);
    this.camera.updateProjectionMatrix();
  }

  private unlockAudioFromInteraction(): void {
    this.audio.ensureStarted();
    this.audio.retryBgmAfterUnlock();
    if (!this.runStartCuePlayed && this.phase === 'playing') {
      this.runStartCuePlayed = true;
      this.audio.playRunStartCue();
    }
    window.removeEventListener('keydown', this.audioUnlockHandler, true);
  }

  private updateTunnelAmbient(local: VehicleState, dt: number): void {
    const inTunnel = isInTunnelUnderpass(local.x, local.z);
    const target = inTunnel ? 1 : 0;
    const k = 1 - Math.exp(-10 * dt);
    this.tunnelBlend += (target - this.tunnelBlend) * k;
    const b = this.tunnelBlend;
    const skyDark = 1 - 0.4 * b;
    this.hemiLight.intensity = 0.4 * skyDark;
    this.ambientLight.intensity = 0.3 * (1 - 0.55 * b);
    this.sunLight.intensity = 0.9 * (1 - 0.38 * b);
    this.fillLight.intensity = 0.2 * (1 - 0.28 * b);
    const fog = this.scene.fog;
    if (fog instanceof THREE.Fog) {
      fog.color.copy(this.fogColorBase).lerp(this.fogColorTunnel, b * 0.88);
      fog.near = this.fogNearBase * (1 - 0.22 * b);
      fog.far = this.fogFarBase * (1 - 0.1 * b);
    }
    this.physicalSky?.setUnderpassBlend(b);
    this.nightSky?.setUnderpassBlend(b);
    const bg = this.scene.background;
    if (bg instanceof THREE.Color) {
      bg.copy(this.fogColorBase).lerp(this.fogColorTunnel, b * 0.45);
    }
  }

  /**
   * Open park lots: road grip (packed paths — grass grip felt like you “couldn’t use” most of the lot).
   * Setback grass around towers: softer grass. Roads/sidewalks: road.
   */
  private syncVehicleSurfaceFromTerrain(v: VehicleState): void {
    const cell = tryBlockCellAtWorldXZ(v.x, v.z);
    if (cell && isParkLot(cell.ix, cell.iz)) {
      v.surfaceType = 'road';
      return;
    }
    v.surfaceType = isWorldXZOnGrassWithoutBuilding(v.x, v.z) ? 'grass' : 'road';
  }

  private resetTunnelAmbient(): void {
    this.tunnelBlend = 0;
    this.hemiLight.intensity = 0.4;
    this.ambientLight.intensity = 0.3;
    this.sunLight.intensity = 0.9;
    this.fillLight.intensity = 0.2;
    const fog = this.scene.fog;
    if (fog instanceof THREE.Fog) {
      fog.color.copy(this.fogColorBase);
      fog.near = this.fogNearBase;
      fog.far = this.fogFarBase;
    }
    this.physicalSky?.setUnderpassBlend(0);
    if (this.scene.background instanceof THREE.Color) {
      this.scene.background.set(SKY);
    }
  }

  private loop(): void {
    this.raf = requestAnimationFrame(this.loop);
    const dtRaw = Math.min(this.clock.getDelta(), 0.05);
    
    // Increment frame counter for staggered updates
    this.frameCount++;

    // Stagger non-critical updates across frames for performance
    const frame2 = this.frameCount % 2 === 0;
    const frame3 = this.frameCount % 3 === 0;
    
    // Sky updates every other frame
    if (frame2) {
      this.physicalSky?.update(dtRaw * 2);
      this.nightSky?.update(dtRaw * 2);
    }
    
    // Rain every other frame
    if (frame2) {
      this.rainSystem?.update(dtRaw * 2, this.camera.position);
    }
    
    // Neon/window lights every 3rd frame
    if (frame3) {
      this.neonSigns?.update(dtRaw * 3);
      this.windowLights?.update(dtRaw * 3);
    }
    this.premiumAtmosphere?.update(dtRaw);
    
    // NPC traffic every 3rd frame
    if (frame3) {
      this.npcTraffic?.update(
        dtRaw * 3,
        this.vehicle.x,
        this.vehicle.z,
        this.copVehicle.x,
        this.copVehicle.z,
      );
    }

    if (this.phase !== 'playing') {
      this.resetTunnelAmbient();
      const weOff = document.getElementById('wanted-edge');
      if (weOff) weOff.dataset.on = 'false';
      const tdOff = document.getElementById('tunnel-dim');
      if (tdOff) tdOff.dataset.on = 'false';
      const sfOff = document.getElementById('speed-fx');
      if (sfOff) sfOff.dataset.on = 'false';
      this.syncChaseCamera(dtRaw, false);
      const gs = Math.hypot(
        this.chaseVehicle().vx,
        this.chaseVehicle().vz,
      );
      this.threatIndicator.update(
        this.camera,
        window.innerWidth,
        window.innerHeight,
        this.chaseVehicle().x,
        this.chaseVehicle().z,
        [],
        false,
        999,
        false,
      );
      this.audio.update(dtRaw, {
        speedMs: gs,
        throttle: 0,
        handbrake: false,
        nitroBoost: false,
        drift: false,
        copDist: this.lastAudioCopDist,
        bustMeter: this.phase === 'lost' ? 1 : 0,
        muted: true,
        sirenPan: 0,
        inTunnel: false,
        wantedStars: this.lastWantedStars,
        suppressAdrenalinePulse: true,
      });
      this.audio.updateMusic(dtRaw, {
        phase: this.phase,
        sessionRemainingS: this.sessionRemainingS,
        bustMeter: this.lastAudioBustMeter,
        wantedStars: this.lastWantedStars,
      });
      this.container.dataset.musicTension = '0';
      this.container.dataset.musicTensionHigh = 'false';
      this.endFrame(dtRaw);
      return;
    }

    let dt = dtRaw;
    if (this.slowMoRemaining > 0 && this.slowMoTotal > 1e-6) {
      const k = Math.min(1, this.slowMoRemaining / this.slowMoTotal);
      const slowFactor = 0.28 + 0.72 * (1 - k) ** 1.15;
      dt *= slowFactor;
      this.slowMoRemaining = Math.max(0, this.slowMoRemaining - dtRaw);
    }

    if (this.lastDirectorOutput && this.lastDirectorOutput.timeScale < 1) {
      const targetScale = this.lastDirectorOutput.timeScale;
      this.cinematicTimeScale = THREE.MathUtils.lerp(
        this.cinematicTimeScale,
        targetScale,
        1 - Math.exp(-6 * dtRaw),
      );
      dt *= this.cinematicTimeScale;
    } else {
      this.cinematicTimeScale = THREE.MathUtils.lerp(
        this.cinematicTimeScale,
        1,
        1 - Math.exp(-4 * dtRaw),
      );
      dt *= this.cinematicTimeScale;
    }

    if (this.juiceFreezeRemain > 0) {
      this.juiceFreezeRemain = Math.max(0, this.juiceFreezeRemain - dtRaw);
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      dt *= reduceMotion ? 0.45 : 0.12;
    }

    let collisionImpulse = 0;

    const input: VehicleInput = {
      throttle: this.keyboard.getThrottle(),
      steer: this.keyboard.getSteer(),
      handbrake: this.keyboard.getHandbrake(),
      nitro: this.keyboard.getNitro(),
    };
    if (this.victoryCelebrationRemain > 0) {
      input.throttle = 0;
      input.steer = 0;
      input.handbrake = true;
      input.nitro = false;
    }

    const mp = this.mpSync();
    const mpDriver = mp && this.mpRole === 'driver';
    const mpCop = mp && this.mpRole === 'cop';
    const aiEvadeDriver =
      this.mpRole === 'cop' && this.mpPeerDisconnected && !mpCop;
    const driverInput = aiEvadeDriver
      ? computeEvadeInput(
          this.vehicle,
          this.nearestCopTo(this.vehicle.x, this.vehicle.z),
        )
      : input;
    const mpCopLocal = mpCop || (this.mpRole === 'cop' && this.mpPeerDisconnected);

    const predGround =
      this.predictionClient?.connected === true && !this.vehicle.airborne;

    if (predGround) {
      const pc = this.predictionClient!;
      pc.reconcileLocalVehicle(this.vehicle);
      this.inputSeq++;
      pc.predictAndSend(this.inputSeq, dt, input, () => {
        integrateGroundStep(this.vehicle, input, dt);
      });
    } else if (mpCop) {
      const rd = this.remoteInterp.getDisplayState(
        REMOTE_ENTITY.DRIVER,
        performance.now(),
      );
      if (rd) {
        this.vehicle.x = rd.x;
        this.vehicle.z = rd.z;
        this.vehicle.vx = rd.vx;
        this.vehicle.vz = rd.vz;
        this.vehicle.heading = rd.heading;
      }
    } else if (this.vehicle.airborne) {
      stepAirborne(this.vehicle, driverInput.steer, dt);
      const xTry = this.vehicle.x + this.vehicle.vx * dt;
      const zTry = this.vehicle.z + this.vehicle.vz * dt;
      if (this.vehicle.airY <= 0) {
        const { impact, badLanding } = landVehicle(this.vehicle);
        this.addCameraShake(
          Math.min(
            0.95,
            impact * 0.065 + (badLanding ? 0.28 : 0) + AIRBORNE.GRAVITY * 0.01,
          ),
        );
        const preVx = this.vehicle.vx;
        const preVz = this.vehicle.vz;
        const fixed = resolveVehicleCollisions(
          xTry,
          zTry,
          preVx,
          preVz,
        );
        this.vehicle.x = fixed.x;
        this.vehicle.z = fixed.z;
        this.vehicle.vx = fixed.vx;
        this.vehicle.vz = fixed.vz;
        const posPush = Math.hypot(fixed.x - xTry, fixed.z - zTry);
        const velDelta = Math.hypot(fixed.vx - preVx, fixed.vz - preVz);
        if (posPush > 0.03 || velDelta > 0.5) {
          collisionImpulse = Math.max(
            collisionImpulse,
            posPush * 1.1 + velDelta * 1.4,
          );
          this.addCameraShake(
            Math.min(
              0.62,
              CAMERA_CHASE.SHAKE_COLLISION_SCALE *
                28 *
                (posPush * 1.1 + velDelta * 1.4),
            ),
          );
        }
      } else {
        const preVx = this.vehicle.vx;
        const preVz = this.vehicle.vz;
        const fixed = resolveVehicleCollisions(xTry, zTry, preVx, preVz, {
          airY: this.vehicle.airY,
        });
        this.vehicle.x = fixed.x;
        this.vehicle.z = fixed.z;
        this.vehicle.vx = fixed.vx;
        this.vehicle.vz = fixed.vz;
        const posPush = Math.hypot(fixed.x - xTry, fixed.z - zTry);
        const velDelta = Math.hypot(fixed.vx - preVx, fixed.vz - preVz);
        if (posPush > 0.03 || velDelta > 0.5) {
          collisionImpulse = Math.max(
            collisionImpulse,
            posPush * 1.1 + velDelta * 1.4,
          );
        }
      }
    } else {
      stepVehiclePhysics(this.vehicle, driverInput, dt, this.driverLoadout.handling);
      tryLaunchFromRamps(this.vehicle, dt);
      const preVx = this.vehicle.vx;
      const preVz = this.vehicle.vz;
      const xTry = this.vehicle.x + this.vehicle.vx * dt;
      const zTry = this.vehicle.z + this.vehicle.vz * dt;
      const fixed = resolveVehicleCollisions(xTry, zTry, preVx, preVz, {
        skipBuildings: this.vehicle.airborne,
      });
      this.vehicle.x = fixed.x;
      this.vehicle.z = fixed.z;
      this.vehicle.vx = fixed.vx;
      this.vehicle.vz = fixed.vz;

      if (!this.vehicle.airborne) {
        const postDest = resolveDestructibleCollisions(
          this.vehicle.x,
          this.vehicle.z,
          this.vehicle.vx,
          this.vehicle.vz,
          this.destructibles,
          (e) => {
            this.stats.propertyDamage += e.chaosBill;
            if (e.destroyed) {
              this.propertyDestroyedAcc += 1;
              this.addCameraShake(e.waterBurst ? 0.22 : 0.16);
              spawnDestructibleBurst(
                this.scene,
                e.x,
                0.45,
                e.z,
                e.kind,
                e.waterBurst,
              );
              if (e.pursuitBreaker) {
                this.stats.pursuitBreakersTriggered += 1;
                this.addCameraShake(0.65);
                this.audio.triggerExplosionBoom();
                this.audio.triggerCollision(0.95);
                // Dramatic slow-mo + destruction cam for pursuit breaker
                this.slowMoTotal = 0.7;
                this.slowMoRemaining = 0.7;
                this.triggerHitStop(0.12, 'breaker');
                this.triggerImpactFlash(0.4);
                this.cameraDirector?.triggerPursuitBreakerCam(e.x, e.z);
                this.stylePop(
                  'pursuit_breaker',
                  'PURSUIT BREAKER',
                  'Environmental takedown lane',
                  'great',
                );
                const shockHits = this.applyPursuitBreakerShockwave(
                  e.x,
                  e.z,
                  e.shockRadius,
                );
                if (shockHits > 0) {
                  this.stats.copsEvaded += shockHits;
                  this.stats.takedowns += shockHits;
                  this.pendingCopTakedown = true;
                  const tdPts = Math.min(500, 180 + shockHits * 110);
                  this.addCameraShake(0.22);
                  this.triggerHitStop(0.075, 'takedown');
                  this.stylePop(
                    'takedown',
                    'TAKEDOWN',
                    'Shockwave hit',
                    'great',
                    tdPts,
                  );
                }
              }
            } else {
              this.addCameraShake(0.035);
            }
          },
        );
        this.vehicle.x = postDest.x;
        this.vehicle.z = postDest.z;
        this.vehicle.vx = postDest.vx;
        this.vehicle.vz = postDest.vz;

        if (this.npcTraffic) {
          const npcResult = resolveNPCTrafficCollision(
            this.vehicle.x,
            this.vehicle.z,
            this.vehicle.vx,
            this.vehicle.vz,
            this.npcTraffic.vehicles,
          );
          this.vehicle.x = npcResult.x;
          this.vehicle.z = npcResult.z;
          this.vehicle.vx = npcResult.vx;
          this.vehicle.vz = npcResult.vz;
          if (npcResult.hitNPC && npcResult.impulse > 0.5) {
            collisionImpulse = Math.max(collisionImpulse, npcResult.impulse);
            this.addCameraShake(Math.min(0.45, npcResult.impulse * 0.08));
            this.audio.triggerCollision(Math.min(1, npcResult.impulse * 0.12));
          }
        }
      }

      const posPush = Math.hypot(fixed.x - xTry, fixed.z - zTry);
      const velDelta = Math.hypot(fixed.vx - preVx, fixed.vz - preVz);
      if (posPush > 0.03 || velDelta > 0.5) {
        collisionImpulse = Math.max(
          collisionImpulse,
          posPush * 1.1 + velDelta * 1.4,
        );
      }
    }

    if (mpDriver) {
      const rc = this.remoteInterp.getDisplayState(
        REMOTE_ENTITY.COP,
        performance.now(),
      );
      if (rc) {
        this.copVehicle.x = rc.x;
        this.copVehicle.z = rc.z;
        this.copVehicle.vx = rc.vx;
        this.copVehicle.vz = rc.vz;
        this.copVehicle.heading = rc.heading;
      }
    } else if (
      this.predictionClient?.connected &&
      this.predictionClient.remoteDisplay
    ) {
      cloneVehicleInto(this.copVehicle, this.predictionClient.remoteDisplay);
    } else if (this.mpRole === 'driver' && this.mpPeerDisconnected) {
      const copStunned = this.isCopStunned(this.copVehicle);
      const copInput = copStunned
        ? { throttle: 0, steer: 0, handbrake: true, nitro: false }
        : computeChaseInput(this.copVehicle, this.vehicle);
      stepVehiclePhysics(this.copVehicle, copInput, dt);
      if (!copStunned) applyCopCatchUp(this.copVehicle, dt, this.vehicle.x, this.vehicle.z);
      const copPreVx = this.copVehicle.vx;
      const copPreVz = this.copVehicle.vz;
      const copXT = this.copVehicle.x + this.copVehicle.vx * dt;
      const copZT = this.copVehicle.z + this.copVehicle.vz * dt;
      const copFixed = resolveCopVehicleCollisions(
        copXT,
        copZT,
        copPreVx,
        copPreVz,
        this.vehicle.x,
        this.vehicle.z,
      );
      this.copVehicle.x = copFixed.x;
      this.copVehicle.z = copFixed.z;
      this.copVehicle.vx = copFixed.vx;
      this.copVehicle.vz = copFixed.vz;
    } else if (mpCopLocal) {
      stepVehiclePhysics(this.copVehicle, input, dt);
      const copPreVx = this.copVehicle.vx;
      const copPreVz = this.copVehicle.vz;
      const copXT = this.copVehicle.x + this.copVehicle.vx * dt;
      const copZT = this.copVehicle.z + this.copVehicle.vz * dt;
      const copFixed = resolveCopVehicleCollisions(
        copXT,
        copZT,
        copPreVx,
        copPreVz,
        this.vehicle.x,
        this.vehicle.z,
      );
      this.copVehicle.x = copFixed.x;
      this.copVehicle.z = copFixed.z;
      this.copVehicle.vx = copFixed.vx;
      this.copVehicle.vz = copFixed.vz;
    } else {
      const copStunned = this.isCopStunned(this.copVehicle);
      const copInput = copStunned
        ? { throttle: 0, steer: 0, handbrake: true, nitro: false }
        : computeChaseInput(this.copVehicle, this.vehicle);
      stepVehiclePhysics(this.copVehicle, copInput, dt);
      if (!copStunned) applyCopCatchUp(this.copVehicle, dt, this.vehicle.x, this.vehicle.z);
      const copPreVx = this.copVehicle.vx;
      const copPreVz = this.copVehicle.vz;
      const copXT = this.copVehicle.x + this.copVehicle.vx * dt;
      const copZT = this.copVehicle.z + this.copVehicle.vz * dt;
      const copFixed = resolveCopVehicleCollisions(
        copXT,
        copZT,
        copPreVx,
        copPreVz,
        this.vehicle.x,
        this.vehicle.z,
      );
      this.copVehicle.x = copFixed.x;
      this.copVehicle.z = copFixed.z;
      this.copVehicle.vx = copFixed.vx;
      this.copVehicle.vz = copFixed.vz;
      if (this.dualCop && this.cop2Vehicle) {
        const cop2Stunned = this.isCopStunned(this.cop2Vehicle);
        const in2 = cop2Stunned
          ? { throttle: 0, steer: 0, handbrake: true, nitro: false }
          : computeChaseInput(this.cop2Vehicle, this.vehicle);
        stepVehiclePhysics(this.cop2Vehicle, in2, dt);
        if (!cop2Stunned) applyCopCatchUp(this.cop2Vehicle, dt, this.vehicle.x, this.vehicle.z);
        const c2pvx = this.cop2Vehicle.vx;
        const c2pvz = this.cop2Vehicle.vz;
        const c2xt = this.cop2Vehicle.x + this.cop2Vehicle.vx * dt;
        const c2zt = this.cop2Vehicle.z + this.cop2Vehicle.vz * dt;
        const c2f = resolveCopVehicleCollisions(
          c2xt,
          c2zt,
          c2pvx,
          c2pvz,
          this.vehicle.x,
          this.vehicle.z,
        );
        this.cop2Vehicle.x = c2f.x;
        this.cop2Vehicle.z = c2f.z;
        this.cop2Vehicle.vx = c2f.vx;
        this.cop2Vehicle.vz = c2f.vz;
      }
    }
    if (!this.predictionClient?.connected) {
      this.separateCopPlayer();
      this.applyCopRoadConstraint();
      
      forceUnstuckCop(this.copVehicle, this.vehicle.x, this.vehicle.z);
      if (this.dualCop && this.cop2Vehicle) {
        forceUnstuckCop(this.cop2Vehicle, this.vehicle.x, this.vehicle.z);
      }
    }
    const takedownCount = this.resolvePlayerCopTakedowns(input);
    if (takedownCount > 0) {
      collisionImpulse = Math.max(collisionImpulse, 7 + takedownCount * 1.6);
    }

    // Update heat system
    const pdDestroyed = this.propertyDestroyedAcc;
    this.propertyDestroyedAcc = 0;
    const heatLevelBefore = this.heatState.level;

    const nearestCopDist = this.getNearestCopDist();
    const speedKmh = Math.hypot(this.vehicle.vx, this.vehicle.vz) * 3.6;
    
    // Calculate drift angle properly (same as camera director)
    const heatSpeed = Math.hypot(this.vehicle.vx, this.vehicle.vz);
    const heatFwdDir = new THREE.Vector3(Math.sin(this.vehicle.heading), 0, Math.cos(this.vehicle.heading));
    const heatVel = new THREE.Vector3(this.vehicle.vx, 0, this.vehicle.vz);
    const heatLateralVel = heatVel.clone().sub(heatFwdDir.clone().multiplyScalar(heatVel.dot(heatFwdDir)));
    const heatDriftAngle = heatSpeed > 1 ? Math.abs(Math.atan2(heatLateralVel.length(), Math.abs(heatVel.dot(heatFwdDir)))) * THREE.MathUtils.RAD2DEG : 0;
    const isDrifting = (input.handbrake && heatSpeed > 8) || heatDriftAngle > 15;
    
    // Detect cop collision (simplified - check if very close and had impact)
    const hitCop = nearestCopDist < 4 && this.lastCollisionImpulse > 0.3 && 
                   this.frameCount - this.lastCopCollisionFrame > 30;
    if (hitCop) this.lastCopCollisionFrame = this.frameCount;
    
    // Detect near miss
    const nearMissCop = nearestCopDist < 8 && nearestCopDist > 3 && speedKmh > 80 &&
                        this.frameCount - this.lastNearMissFrame > 60;
    if (nearMissCop) this.lastNearMissFrame = this.frameCount;

    updateHeatState(this.heatState, {
      dt,
      speedKmh,
      isDrifting,
      nearestCopDist,
      hitCop,
      nearMissCop,
      copTakedown: this.pendingCopTakedown,
      propertyDestroyed: pdDestroyed,
    });
    this.pendingCopTakedown = false;

    if (this.heatState.level > heatLevelBefore) {
      this.stylePop(
        'heat_up',
        'HEAT LEVEL UP!',
        `${this.heatState.level} ★ — more units inbound`,
        'heat',
      );
    }

    if (!this.mpInSession() && !this.predictionClient?.connected) {
      const hazardWarning = this.policeHazards.update({
        dt: dtRaw,
        frame: this.frameCount,
        playerX: this.vehicle.x,
        playerZ: this.vehicle.z,
        playerHeading: this.vehicle.heading,
        roadblocksEnabled: this.heatState.config.roadblocksEnabled,
        spikeStripsEnabled: this.heatState.config.spikeStripsEnabled,
      });
      if (hazardWarning) {
        this.scoreFeed.show(
          hazardWarning.kind === 'spikes' ? 'SPIKE STRIP AHEAD' : 'ROADBLOCK AHEAD',
          'Break, dodge, or smash through',
          'danger',
          undefined,
          'mega',
        );
        this.audio.bumpMusicImpactDuck(0.28);
      }

      const hazardResult = this.policeHazards.resolvePlayer({
        frame: this.frameCount,
        x: this.vehicle.x,
        z: this.vehicle.z,
        vx: this.vehicle.vx,
        vz: this.vehicle.vz,
        heading: this.vehicle.heading,
        nitroTank: this.vehicle.nitroTank,
        nitroBoosting: this.vehicle.nitroBoosting,
      });
      this.vehicle.x = hazardResult.x;
      this.vehicle.z = hazardResult.z;
      this.vehicle.vx = hazardResult.vx;
      this.vehicle.vz = hazardResult.vz;
      this.vehicle.nitroTank = hazardResult.nitroTank;
      if (hazardResult.hit) {
        collisionImpulse = Math.max(collisionImpulse, hazardResult.hit.impulse);
        this.addCameraShake(
          hazardResult.hit.nitroPierce
            ? 0.62
            : hazardResult.hit.kind === 'spikes'
              ? 0.42
              : 0.78,
        );
        this.triggerImpactFlash(hazardResult.hit.kind === 'spikes' ? 0.18 : 0.28);
        this.triggerHitStop(hazardResult.hit.kind === 'spikes' ? 0.045 : 0.078, 'hazard');
        this.audio.triggerCollision(hazardResult.hit.kind === 'spikes' ? 0.72 : 1);
        this.audio.bumpMusicImpactDuck(hazardResult.hit.kind === 'spikes' ? 0.45 : 0.75);
        if (hazardResult.hit.nitroPierce) {
          this.stylePop(
            'takedown',
            'NITRO BREAKTHROUGH',
            'Roadblock smashed',
            'great',
            360,
          );
        } else {
          this.scoreFeed.show(
            hazardResult.hit.kind === 'spikes' ? 'SPIKE STRIP!' : 'ROADBLOCK HIT',
            hazardResult.hit.kind === 'spikes' ? 'Nitro tank punctured' : 'Police blockade impact',
            'danger',
            undefined,
            'mega',
          );
        }
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // UPDATE GAME STATS for victory screen
    // ═══════════════════════════════════════════════════════════════════════════
    if (this.phase === 'playing') {
      // Track max speed
      if (speedKmh > this.stats.maxSpeedKmh) {
        this.stats.maxSpeedKmh = speedKmh;
      }
      
      // Track closest cop distance
      if (nearestCopDist < this.stats.closestCopDistance && nearestCopDist > 0) {
        this.stats.closestCopDistance = nearestCopDist;
      }
      
      // Track near misses
      if (nearMissCop) {
        this.stats.nearMisses++;
        this.stylePop('near_miss', '+100 NEAR MISS', 'Heat + skill', 'good');
      }
      
      // Track collisions
      if (hitCop) {
        this.stats.totalCollisions++;
      }
      
      // Track drift distance
      if (isDrifting) {
        if (this.wasDrifting) {
          const driftDist = Math.hypot(
            this.vehicle.x - this.lastDriftPos.x,
            this.vehicle.z - this.lastDriftPos.z
          );
          this.stats.totalDriftDistance += driftDist;
        }
        this.lastDriftPos.x = this.vehicle.x;
        this.lastDriftPos.z = this.vehicle.z;
        this.wasDrifting = true;
      } else {
        this.wasDrifting = false;
      }

      const driftSeg = Math.floor(this.stats.totalDriftDistance / 72);
      if (driftSeg > this.lastDriftScoreMilestone) {
        this.lastDriftScoreMilestone = driftSeg;
        this.stylePop(
          'drift_chain',
          '+250 DRIFT',
          `${Math.floor(this.stats.totalDriftDistance)} m chained`,
          'good',
        );
      }
      this.maybeAnnounceContractComplete();
    }
    this.maybeShowContractRunCue();

    // Spawn/update extra cops based on heat level
    this.updateCopCountForHeat();
    this.updateExtraCops(dt);
    
    // Update helicopter (appears at heat level 5, but not during start sequence)
    const gameTimeS = (performance.now() - this.stats.startTime) / 1000;
    const heliActive = this.heatState.config.helicopterEnabled && 
                       this.phase === 'playing' && 
                       gameTimeS > 3; // Wait 3 seconds for tire reveal to finish
    if (this.helicopter) {
      this.helicopter.update(dt, this.vehicle.x, this.vehicle.z, heliActive);
    }
    
    this.syncVehicleSurfaceFromTerrain(this.vehicle);
    this.syncVehicleSurfaceFromTerrain(this.copVehicle);
    if (this.dualCop && this.cop2Vehicle) {
      this.syncVehicleSurfaceFromTerrain(this.cop2Vehicle);
    }
    for (const ec of this.copPool) {
      if (ec.active) this.syncVehicleSurfaceFromTerrain(ec.vehicle);
    }

    let distCop = Math.hypot(
      this.vehicle.x - this.copVehicle.x,
      this.vehicle.z - this.copVehicle.z,
    );
    if (this.dualCop && this.cop2Vehicle) {
      distCop = Math.min(
        distCop,
        Math.hypot(
          this.vehicle.x - this.cop2Vehicle.x,
          this.vehicle.z - this.cop2Vehicle.z,
        ),
      );
    }
    const bustRes = this.predictionClient?.connected
      ? { nearMiss: false, busted: false, meter: 0 }
      : stepBustMeter(this.bust, distCop, dtRaw);
    if (bustRes.nearMiss) {
      this.slowMoTotal = 0.42;
      this.slowMoRemaining = 0.42;
      this.juicePunchScreen('near');
      this.closeCallRemaining = 2.2;
      if (this.closeCallEl) this.closeCallEl.dataset.state = 'show';
      if (this.impactFlashEl) {
        this.impactFlashEl.dataset.state = 'burst';
        window.setTimeout(() => {
          if (this.impactFlashEl) this.impactFlashEl.dataset.state = 'hide';
        }, 140);
      }
    }
    if (this.closeCallRemaining > 0) {
      this.closeCallRemaining -= dtRaw;
      if (this.closeCallRemaining <= 0 && this.closeCallEl) {
        this.closeCallEl.dataset.state = 'hide';
      }
    }

    if (
      this.phase === 'playing' &&
      this.victoryCelebrationRemain <= 0 &&
      !this.predictionClient?.connected &&
      (bustRes.busted || distCop < GAME.BUST_RADIUS * 0.85)
    ) {
      if (this.mpInSession()) {
        this.sendRoundOutcome('cop', 'bust');
      }
      this.beginLossSequence('BUSTED', 'bust');
    }

    if (
      this.phase === 'playing' &&
      !this.vehicle.airborne &&
      !this.predictionClient?.connected
    ) {
      const dEx = Math.hypot(
        this.vehicle.x - this.roundExtractionX,
        this.vehicle.z - this.roundExtractionZ,
      );
      if (dEx < GAME.EXTRACTION_RADIUS) {
        if (!this.isExtractionUnlocked()) {
          this.showExtractionLocked();
        } else {
          this.extractionHold += dtRaw;
        }
        const holdWrap = document.getElementById('extraction-hold');
        if (holdWrap) holdWrap.hidden = false;
        if (this.extractionFillEl) {
          this.extractionFillEl.style.transform = `scaleX(${Math.min(
            1,
            this.extractionHold / GAME.EXTRACTION_HOLD_S,
          )})`;
        }
        if (this.extractionHold >= GAME.EXTRACTION_HOLD_S) {
          if (this.victoryCelebrationRemain > 0) {
            const holdWrapDone = document.getElementById('extraction-hold');
            if (holdWrapDone) holdWrapDone.hidden = true;
          } else if (
            this.mpInSession() &&
            this.mpRole === 'cop' &&
            !this.mpSync()
          ) {
            this.beginLossSequence('DRIVER ESCAPED', 'driver_escaped');
          } else if (this.mpInSession() && this.mpRole === 'driver') {
            this.beginDriverVictoryCelebration();
            this.sendRoundOutcome('driver', 'extraction');
          } else if (!this.mpInSession()) {
            this.beginDriverVictoryCelebration();
          }
        }
      } else {
        this.extractionHold = 0;
        const holdWrap = document.getElementById('extraction-hold');
        if (holdWrap) holdWrap.hidden = true;
        if (this.extractionFillEl) this.extractionFillEl.style.transform = 'scaleX(0)';
      }
    }

    if (!this.predictionClient?.connected && this.victoryCelebrationRemain <= 0) {
      this.sessionRemainingS = Math.max(0, this.sessionRemainingS - dtRaw);
      if (this.sessionRemainingS <= 0 && this.phase === 'playing') {
        if (this.mpInSession()) {
          this.sendRoundOutcome('cop', 'time_up');
        }
        this.beginLossSequence('TIME UP', 'time_up');
      }
    }

    if (this.mpSync() && this.phase === 'playing' && this.mpRoom) {
      this.mpSnapAcc += dtRaw;
      while (this.mpSnapAcc >= 1 / MP_SNAPSHOT_HZ) {
        this.mpSnapAcc -= 1 / MP_SNAPSHOT_HZ;
        const v = this.chaseVehicle();
        try {
          this.mpRoom.send('snapshot', {
            sessionId: this.mpSession!,
            x: v.x,
            z: v.z,
            vx: v.vx,
            vz: v.vz,
            heading: v.heading,
            airY: v.airY ?? 0,
          });
        } catch {
          break;
        }
      }
    }

    this.copCar.position.set(this.copVehicle.x, CAR_Y, this.copVehicle.z);
    this.copCar.rotation.y = this.copVehicle.heading;
    this.copLightBar?.setPosition(this.copVehicle.x, CAR_Y, this.copVehicle.z, this.copVehicle.heading);
    // Update light bars every other frame for performance
    if (this.frameCount % 2 === 0) {
      this.copLightBar?.update(dt * 2);
    }

    if (this.cop2Car && this.cop2Vehicle) {
      this.cop2Car.position.set(this.cop2Vehicle.x, CAR_Y, this.cop2Vehicle.z);
      this.cop2Car.rotation.y = this.cop2Vehicle.heading;
      this.cop2LightBar?.setPosition(this.cop2Vehicle.x, CAR_Y, this.cop2Vehicle.z, this.cop2Vehicle.heading);
      if (this.frameCount % 2 === 0) {
        this.cop2LightBar?.update(dt * 2);
      }
    }

    const local = this.chaseVehicle();
    this.updateTunnelAmbient(local, dt);
    if (!this.prevNitroBoosting && local.nitroBoosting) {
      this.addCameraShake(CAMERA_CHASE.SHAKE_NITRO_ON * 1.35);
      this.triggerHitStop(0.024, 'nitro');
      this.audio.bumpMusicImpactDuck(0.35);
    }
    this.prevNitroBoosting = local.nitroBoosting;

    this.car.position.set(
      this.vehicle.x,
      CAR_Y + this.vehicle.airY,
      this.vehicle.z,
    );
    this.landingShadow.position.set(local.x, 0.045, local.z);
    this.landingShadow.visible = local.airborne;
    const h = local.heading;
    const fwdSpeed =
      local.vx * Math.sin(h) + local.vz * Math.cos(h);
    const rawLongAccel =
      (fwdSpeed - this.prevFwdSpeed) / Math.max(dt, 1e-4);
    this.prevFwdSpeed = fwdSpeed;
    const accBlend = 1 - Math.exp(-9 * dt);
    this.smoothedLongAccel +=
      (rawLongAccel - this.smoothedLongAccel) * accBlend;

    let targetPitch = -THREE.MathUtils.clamp(
      this.smoothedLongAccel * 0.0085,
      -0.095,
      0.095,
    );
    const th = input.throttle;
    if (th > 0.06) targetPitch -= 0.028 * th;
    else if (th < -0.06 && fwdSpeed > 0.4) targetPitch += 0.038;

    const pitchBlend = 1 - Math.exp(-7 * dt);
    this.bodyPitch += (targetPitch - this.bodyPitch) * pitchBlend;
    const airPitch = local.airborne
      ? THREE.MathUtils.clamp(local.vy * 0.011, -0.18, 0.28)
      : 0;
    if (this.mpRole === 'cop') {
      this.copCar.rotation.y = h;
      this.copCar.rotation.x = this.bodyPitch + airPitch;
      this.car.rotation.y = this.vehicle.heading;
      this.car.rotation.x = 0;
    } else {
      this.car.rotation.y = h;
      this.car.rotation.x = this.bodyPitch + airPitch;
      this.copCar.rotation.y = this.copVehicle.heading;
      this.copCar.rotation.x = 0;
      if (this.cop2Car && this.cop2Vehicle) {
        this.cop2Car.rotation.y = this.cop2Vehicle.heading;
        this.cop2Car.rotation.x = 0;
      }
    }

    /** Spin tyres whenever the car moves: pure lateral drift has hypot>0 but fwd≈0. */
    const slipMag = Math.hypot(local.vx, local.vz);
    const playerWheelSpin = Math.max(Math.abs(fwdSpeed), slipMag);
    const copFwd =
      this.copVehicle.vx * Math.sin(this.copVehicle.heading) +
      this.copVehicle.vz * Math.cos(this.copVehicle.heading);
    const copSlip = Math.hypot(this.copVehicle.vx, this.copVehicle.vz);
    const copWheelSpin = Math.max(Math.abs(copFwd), copSlip);
    updateCarWheels(this.car, playerWheelSpin, dt, input.steer);
    updateCarWheels(this.copCar, copWheelSpin, dt, 0);
    const lightTime = performance.now() / 1000;
    updateCarLights(this.car, {
      throttle: driverInput.throttle,
      steer: driverInput.steer,
      handbrake: driverInput.handbrake,
      fwdSpeed:
        this.vehicle.vx * Math.sin(this.vehicle.heading) +
        this.vehicle.vz * Math.cos(this.vehicle.heading),
      speed: Math.hypot(this.vehicle.vx, this.vehicle.vz),
      timeS: lightTime,
    });
    updateCarLights(this.copCar, {
      throttle: mpCopLocal ? input.throttle : 1,
      steer: mpCopLocal ? input.steer : 0,
      handbrake: false,
      fwdSpeed: copFwd,
      speed: copSlip,
      timeS: lightTime + 0.17,
    });
    if (this.cop2Car && this.cop2Vehicle) {
      const c2 = this.cop2Vehicle;
      const c2Fwd = c2.vx * Math.sin(c2.heading) + c2.vz * Math.cos(c2.heading);
      const c2Slip = Math.hypot(c2.vx, c2.vz);
      const cop2WheelSpin = Math.max(Math.abs(c2Fwd), c2Slip);
      updateCarWheels(this.cop2Car, cop2WheelSpin, dt, 0);
      updateCarLights(this.cop2Car, {
        throttle: 1,
        steer: 0,
        handbrake: false,
        fwdSpeed: c2Fwd,
        speed: c2Slip,
        timeS: lightTime + 0.34,
      });
    }

    if (collisionImpulse > 0.1) {
      this.lastCollisionTime = 0;
      this.lastCollisionImpulse = collisionImpulse;
    } else {
      this.lastCollisionTime += dt;
    }

    const copDist = Math.hypot(local.x - this.copVehicle.x, local.z - this.copVehicle.z);
    const copProximity = Math.max(0, 1 - copDist / 20);

    const speed = Math.hypot(local.vx, local.vz);
    const forwardDir = new THREE.Vector3(Math.sin(local.heading), 0, Math.cos(local.heading));
    const velocity = new THREE.Vector3(local.vx, 0, local.vz);
    const lateralVel = velocity.clone().sub(forwardDir.clone().multiplyScalar(velocity.dot(forwardDir)));
    const driftAngle = speed > 1 ? Math.abs(Math.atan2(lateralVel.length(), Math.abs(velocity.dot(forwardDir)))) * THREE.MathUtils.RAD2DEG : 0;

    const extractionDir = new THREE.Vector3(
      this.roundExtractionX - local.x,
      0,
      this.roundExtractionZ - local.z,
    ).normalize();

    if (this.useCameraDirector && this.cameraDirector) {
      // Get helicopter position if active
      const heliPos = (this.helicopter?.isActive()) 
        ? this.helicopter.getPosition() 
        : null;
      
      const directorInput: DirectorInput = {
        carPosition: new THREE.Vector3(local.x, CAR_Y + local.airY, local.z),
        carVelocity: new THREE.Vector3(local.vx, 0, local.vz),
        carRotation: local.heading,
        speedKmh: speed * 3.6,
        isDrifting: input.handbrake && speed > 8,
        driftAngle,
        isNitroActive: local.nitroBoosting,
        isAirborne: local.airborne,
        timeSinceLastCollision: this.lastCollisionTime,
        collisionImpactForce: this.lastCollisionImpulse,
        copProximity,
        bustProgress: this.bust.meter,
        timeRemaining: this.sessionRemainingS,
        extractionDirection: extractionDir,
        helicopterPosition: heliPos,
      };

      this.lastDirectorOutput = this.cameraDirector.update(
        directorInput,
        this.camera,
        dt,
      );
    } else {
      this.lastDirectorOutput = null;
      this.syncChaseCamera(dt, local.nitroBoosting);
    }

    const groundSpeed = Math.hypot(local.vx, local.vz);
    const rpmRatio = Math.min(1, groundSpeed / PHYSICS.MAX_SPEED_MS);
    const isRevving = input.throttle > 0.7 && groundSpeed < 8;
    
    this.drivingVfx.update(dt, {
      x: local.x,
      z: local.z,
      heading: local.heading,
      speed: groundSpeed,
      handbrake: input.handbrake,
      nitroBoosting: local.nitroBoosting,
      throttle: input.throttle,
      collisionImpulse,
      revving: isRevving,
      rpmRatio,
    });
    const sr =
      PHYSICS.NITRO_MAX_SPEED_MS > 1e-6
        ? groundSpeed / PHYSICS.NITRO_MAX_SPEED_MS
        : 0;
    this.container.dataset.speed =
      sr < 0.42 ? 'low' : sr < 0.72 ? 'mid' : 'high';

    this.radialSpeedLines?.update(dtRaw, sr);

    const spin = this.cityGroup.userData.landmarkSpin as
      | THREE.Object3D[]
      | undefined;
    spin?.forEach((o) => {
      o.rotation.y += dt * 0.55;
    });

    const kph = Math.hypot(local.vx, local.vz) * 3.6;

    // Heat level system - use actual heat level as wanted stars
    const wantedStars = this.heatState.level;
    this.lastWantedStars = wantedStars;

    const threatCops: { x: number; z: number }[] = [
      { x: this.copVehicle.x, z: this.copVehicle.z },
    ];
    if (this.dualCop && this.cop2Vehicle) {
      threatCops.push({ x: this.cop2Vehicle.x, z: this.cop2Vehicle.z });
    }
    // Add extra cops from heat system
    for (const ec of this.copPool) {
      if (ec.active) threatCops.push({ x: ec.vehicle.x, z: ec.vehicle.z });
    }
    const closingFast =
      (this.prevDistCopThreat - distCop) / Math.max(dtRaw, 1e-4) > 14;
    this.threatIndicator.update(
      this.camera,
      window.innerWidth,
      window.innerHeight,
      local.x,
      local.z,
      threatCops,
      this.mpRole !== 'cop' &&
        this.phase === 'playing' &&
        this.victoryCelebrationRemain <= 0,
      distCop,
      closingFast,
    );
    this.prevDistCopThreat = distCop;

    if (this.hudClosingEl) {
      const closingIn =
        this.mpRole !== 'cop' &&
        this.phase === 'playing' &&
        this.victoryCelebrationRemain <= 0 &&
        closingFast &&
        distCop < 95 &&
        distCop > 14;
      this.hudClosingEl.dataset.visible = closingIn ? 'true' : 'false';
    }

    if (this.speedStreaksEl) {
      const streak = THREE.MathUtils.clamp((sr - 0.2) / 0.8, 0, 1);
      this.speedStreaksEl.style.opacity = String(streak * 0.68);
    }

    const hudShell = document.getElementById('hud-shell');
    if (hudShell) {
      hudShell.dataset.heatPulse = String(
        Math.round(this.heatState.levelUpFlash * 100),
      );
    }

    const we = document.getElementById('wanted-edge');
    if (we) we.dataset.on = wantedStars >= 5 ? 'true' : 'false';
    const speedFx = document.getElementById('speed-fx');
    if (speedFx) {
      speedFx.dataset.on =
        kph > 190 && groundSpeed / PHYSICS.NITRO_MAX_SPEED_MS > 0.88
          ? 'true'
          : 'false';
    }
    const td = document.getElementById('tunnel-dim');
    if (td) {
      td.dataset.on = isInTunnelUnderpass(local.x, local.z) ? 'true' : 'false';
    }

    const timeT = 1 - this.sessionRemainingS / SESSION_LENGTH_S;
    const chaseT = bustRes.meter * 0.52 + (wantedStars / 5) * 0.28;
    const last30 = this.sessionRemainingS < MUSIC.LAST_30_S ? 0.2 : 0;
    const last10 = this.sessionRemainingS < MUSIC.LAST_10_S ? 0.32 : 0;
    const tension = Math.min(1, timeT * 0.48 + chaseT * 0.42 + last30 + last10);
    this.container.dataset.musicTension = String(Math.round(tension * 100));
    this.container.dataset.musicTensionHigh = tension > 0.82 ? 'true' : 'false';

    if (collisionImpulse > 0.35) {
      this.audio.triggerCollision(Math.min(1, collisionImpulse * 0.14));
    }
    if (collisionImpulse > 0.52) {
      this.audio.bumpMusicImpactDuck(Math.min(1, collisionImpulse * 0.2));
    }
    if (collisionImpulse > 0.68) {
      this.triggerHitStop(0.032, 'impact');
      this.addCameraShake(0.12);
    }
    if (collisionImpulse > 0.42) {
      const visual =
        this.mpRole === 'cop' ? this.copCar : this.car;
      this.applyVehicleDamage(visual, collisionImpulse);
    }
    this.lastAudioCopDist = distCop;
    this.lastAudioBustMeter = bustRes.meter;
    const nearestCop = this.nearestCopTo(local.x, local.z);
    const sirenPan = this.computeSirenPan(local.x, local.z, nearestCop);
    const inTunnel = isInTunnelUnderpass(local.x, local.z);
    this.audio.update(dtRaw, {
      speedMs: groundSpeed,
      throttle: input.throttle,
      handbrake: input.handbrake,
      nitroBoost: local.nitroBoosting,
      drift: input.handbrake && groundSpeed > 4,
      copDist: distCop,
      bustMeter: bustRes.meter,
      muted: false,
      sirenPan,
      inTunnel,
      wantedStars,
      suppressAdrenalinePulse: this.victoryCelebrationRemain > 0,
    });
    this.audio.updateMusic(dtRaw, {
      phase: this.phase,
      sessionRemainingS: this.sessionRemainingS,
      bustMeter: bustRes.meter,
      wantedStars,
    });
    this.audio.tickRadioChatter(dtRaw, {
      phase: this.phase,
      bustMeter: bustRes.meter,
      wantedStars,
    });

    // Update helicopter audio
    const heliIsActive = this.helicopter?.isActive() ?? false;
    const heliDist = heliIsActive ? this.helicopter!.getPosition().distanceTo(new THREE.Vector3(local.x, 0, local.z)) : 999;
    this.audio.updateHelicopter(heliIsActive, heliDist);

    const drifting = input.handbrake && kph > 18;
    const nitroBoostHud = local.nitroBoosting;
    updateComboDecay(this.comboState, dtRaw);
    const comboHud = getComboHudDisplay(this.comboState);
    this.hud.update(
      kph,
      input.handbrake,
      input.throttle,
      local.nitroTank,
      drifting,
      nitroBoostHud,
      local.nitroCooldown,
      this.sessionRemainingS,
      wantedStars,
      bustRes.meter,
      comboHud.mult,
      comboHud.chain,
      comboHud.active,
    );
    const contractStats = this.currentContractStats();
    this.hud.updateContract(
      this.runContract.title,
      runContractProgress(this.runContract, contractStats),
      isRunContractComplete(this.runContract, contractStats),
    );

    const bonusLive = Math.max(
      0,
      Math.floor(this.sessionRemainingS * 100),
    );
    const hudBonus = document.getElementById('hud-time-bonus-live');
    if (hudBonus) {
      hudBonus.textContent = `Time bonus (if you escape): ${bonusLive.toLocaleString()} pts`;
      hudBonus.dataset.urgent =
        this.sessionRemainingS < MUSIC.LAST_30_S ? 'true' : 'false';
    }

    const pulseT = performance.now() * 0.003;
    this.extractionPulse.scale.setScalar(0.92 + Math.sin(pulseT) * 0.08);
    this.extractionRing.rotation.z = pulseT * 0.08;
    this.premiumRoute?.update(
      dtRaw,
      local.x,
      local.z,
      this.roundExtractionX,
      this.roundExtractionZ,
      this.isExtractionUnlocked(),
    );

    if (!this.minimapCtx) this.setupMinimapCanvas();
    if (this.minimapCtx) {
      const opp = this.opponentVehicle();
      const cops: { x: number; z: number }[] = [
        { x: opp.x, z: opp.z },
      ];
      if (this.mpRole !== 'cop' && this.dualCop && this.cop2Vehicle) {
        cops.push({ x: this.cop2Vehicle.x, z: this.cop2Vehicle.z });
      }
      // Add extra cops from heat system to minimap
      for (const ec of this.copPool) {
        if (ec.active) cops.push({ x: ec.vehicle.x, z: ec.vehicle.z });
      }
      const centerPx = local.x + this.minimapPanWorldX;
      const centerPz = local.z + this.minimapPanWorldZ;
      const propDots = this.destructibles
        .filter((d) => d.alive)
        .slice(0, 160)
        .map((d) => ({
          x: d.x,
          z: d.z,
          breaker: isPursuitBreaker(d.kind),
        }));
      drawMinimap(
        this.minimapCtx,
        this.minimapW,
        this.minimapH,
        centerPx,
        centerPz,
        local.heading,
        { x: local.x, z: local.z, heading: local.heading },
        cops,
        this.dualCop
          ? { x: this.roundExtractionX, z: this.roundExtractionZ }
          : undefined,
        propDots,
      );
    }

    if (this.debugHud) {
      let dh = document.getElementById('debug-hud');
      if (!dh) {
        dh = document.createElement('div');
        dh.id = 'debug-hud';
        dh.style.cssText =
          'position:fixed;bottom:10px;left:10px;z-index:120;padding:8px 10px;font:11px/1.45 JetBrains Mono,monospace;color:#bfffea;background:rgba(6,10,18,0.85);border:1px solid rgba(0,255,200,0.35);border-radius:8px;pointer-events:none;max-width:min(96vw,420px);white-space:pre-wrap';
        document.body.appendChild(dh);
      }
      const tun = isInTunnelUnderpass(local.x, local.z);
      dh.textContent = [
        `tunnel (underpass): ${tun}`,
        `pos x ${local.x.toFixed(0)}  z ${local.z.toFixed(0)}`,
        `goal x ${this.roundExtractionX.toFixed(0)}  z ${this.roundExtractionZ.toFixed(0)}`,
        'strips: z≈760 (any x) or x≈740 (any z)',
      ].join('\n');
    }

    if (this.victoryCelebrationRemain > 0) {
      this.victoryHeadlightT += dtRaw;
      this.updateVictoryHeadlightPulse(this.victoryHeadlightT);
      this.victoryCelebrationRemain -= dtRaw;
      if (this.victoryCelebrationRemain <= 0) {
        this.victoryCelebrationRemain = 0;
        this.finishVictoryCelebrationDriver();
      }
    }

    this.endFrame(dtRaw);
  }

  private endFrame(dt: number): void {
    this.syncDirectorPostFx(dt);
    if (this.composer) {
      this.composer.render(dt);
    } else {
      this.renderer.render(this.scene, this.camera);
    }
    this.applyDirectorFxCssFallback();
  }

  /** Lerp + push `CameraDirector` lens values into the post stack (or CSS when PP off). */
  private syncDirectorPostFx(dt: number): void {
    const out = this.lastDirectorOutput;
    const k = 1 - Math.exp(-11 * dt);
    const z = (v: number, t: number) => THREE.MathUtils.lerp(v, t, k);

    if (!out) {
      this.smoothedDirectorFx.chroma = z(this.smoothedDirectorFx.chroma, 0);
      this.smoothedDirectorFx.radial = z(this.smoothedDirectorFx.radial, 0);
      this.smoothedDirectorFx.vig = z(this.smoothedDirectorFx.vig, 0);
      this.smoothedDirectorFx.red = z(this.smoothedDirectorFx.red, 0);
    } else {
      this.smoothedDirectorFx.chroma = z(
        this.smoothedDirectorFx.chroma,
        out.chromaticAberration,
      );
      this.smoothedDirectorFx.radial = z(
        this.smoothedDirectorFx.radial,
        out.radialBlurIntensity,
      );
      this.smoothedDirectorFx.vig = z(this.smoothedDirectorFx.vig, out.vignette);
      this.smoothedDirectorFx.red = z(this.smoothedDirectorFx.red, out.redTint);
    }

    const mat = this.directorPass?.material as THREE.ShaderMaterial | undefined;
    if (mat?.uniforms) {
      mat.uniforms.chromaticAmt.value = this.smoothedDirectorFx.chroma;
      mat.uniforms.radialBlurAmt.value = this.smoothedDirectorFx.radial;
      mat.uniforms.vignetteAmt.value = this.smoothedDirectorFx.vig;
      mat.uniforms.redTintAmt.value = this.smoothedDirectorFx.red;
    }
  }

  /** Mobile / reduced-motion path: approximate director vignette on `#app`. */
  private applyDirectorFxCssFallback(): void {
    if (this.composer) {
      this.container.removeAttribute('data-director-fx');
      this.container.style.removeProperty('--fx-vignette');
      this.container.style.removeProperty('--fx-chroma');
      return;
    }
    this.container.setAttribute('data-director-fx', '1');
    const v = this.smoothedDirectorFx.vig;
    const c = this.smoothedDirectorFx.chroma;
    this.container.style.setProperty('--fx-vignette', String(v * 0.85));
    this.container.style.setProperty('--fx-chroma', String(c * 120));
  }
}
